import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, MessageSquare, Calendar, 
  User, Users, Shield, Clock, CheckCircle, AlertCircle, 
  Upload, ChevronRight, Bell, Activity, ShoppingCart,
  Mail, Heart, Stethoscope, ClipboardList, CreditCard,
  Brain, Zap, AlertTriangle, TrendingUp, Lightbulb,
  FlaskConical, Target, Pill, BookOpen, ExternalLink, Info,
  ZoomIn, X, Download, Trash2, ShieldCheck, Key, Copy, Lock,
  Video, CalendarClock, Gift, DollarSign, Menu, ChevronLeft, Shuffle, Plus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { grantSiteAccess } from './ComingSoon';
import ClinicalReportButton from '../components/ClinicalReportButton';
import InstallAppSection from '../components/InstallAppSection';
import GuestSaveSpotNudge from '../components/GuestSaveSpotNudge';
import GuestSaveSpotBreadcrumb from '../components/GuestSaveSpotBreadcrumb';
import OfflineIntakeBadge from '../components/OfflineIntakeBadge';
import {
  loadDraft as loadIntakeDraft,
  saveDraft as saveIntakeDraft,
  clearDraft as clearIntakeDraft,
  loadPendingSubmit,
  queuePendingSubmit,
  clearPendingSubmit,
} from '../lib/intakeDraftStore';
import '../styles/PortalCopper.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Portal = () => {
  const { user, token, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [cases, setCases] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentIntakeId, setPaymentIntakeId] = useState(null);
  // Initialize sidebar state synchronously from the viewport so the dashboard
  // layout paints in its FINAL state on the first frame (open on desktop,
  // collapsed on mobile). Deriving this in a post-paint effect previously
  // caused a collapsed→open layout jump on client-side navigation.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : true
  );

  // Handle URL query params for tab navigation (e.g., /portal?tab=affiliate)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['dashboard', 'welcome', 'intake', 'purchase', 'cases', 'consultations', 'messages', 'documents', 'resources', 'affiliate', 'profile'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Check for guest mode
  const params = new URLSearchParams(location.search);
  const isGuestMode = params.get('guest') === 'true';

  useEffect(() => {
    // Only redirect if we're definitely not authenticated AND not in guest mode
    if (!loading && !isAuthenticated && !localStorage.getItem('cadence_token') && !isGuestMode) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate, isGuestMode]);

  // Add portal-active class to body for CSS targeting.
  // useLayoutEffect (not useEffect) so the App's velvet-gold background is
  // hidden BEFORE the browser paints the portal — otherwise, on client-side
  // navigation the marketing background flashed behind the copper dashboard
  // for one frame, making the sidebar/menu look "messed up" until refresh.
  useLayoutEffect(() => {
    document.body.classList.add('portal-active');
    return () => {
      document.body.classList.remove('portal-active');
    };
  }, []);

  // Handle return from Stripe payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const intakeId = urlParams.get('intake_id');
    
    if (paymentStatus === 'success' && intakeId && token) {
      // Confirm payment and finalize intake
      confirmIntakePayment(intakeId);
      // Clean up URL
      window.history.replaceState({}, document.title, '/portal');
    } else if (paymentStatus === 'cancelled') {
      alert('Payment was cancelled. Your intake form has been saved. Please try again to complete your submission.');
      window.history.replaceState({}, document.title, '/portal');
    }
  }, [token]);

  const confirmIntakePayment = async (intakeId) => {
    try {
      const response = await fetch(`${API_URL}/api/portal/intake/confirm-payment?intake_id=${intakeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setPaymentSuccess(true);
        setPaymentIntakeId(intakeId);
        setActiveTab('intake'); // Show thank you screen
      } else {
        const errorData = await response.json();
        console.error('Payment confirmation error:', errorData);
        alert(errorData.detail || 'Payment verification failed. Please contact support.');
      }
    } catch (err) {
      console.error('Payment confirmation error:', err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboardData();
      fetchCases();
      fetchMessages();
    } else if (isGuestMode) {
      // In guest mode without token, skip loading and show intake form directly
      setIsLoading(false);
    }
  }, [token, isGuestMode]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  };

  const fetchCases = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/cases`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCases(data);
      }
    } catch (err) {
      console.error('Cases fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (err) {
      console.error('Messages fetch error:', err);
    }
  };

  const handlePurchase = async (serviceType, price, addOns = []) => {
    try {
      const response = await fetch(`${API_URL}/api/consultations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_type: serviceType,
          medical_concerns: '',
          preferred_date: new Date().toISOString(),
          add_ons: addOns
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Support both Square and Stripe checkout URLs for backwards compatibility
        if (data.square_checkout_url) {
          window.location.href = data.square_checkout_url;
        } else if (data.stripe_session_url) {
          window.location.href = data.stripe_session_url;
        }
      } else {
        console.error('Purchase failed');
      }
    } catch (err) {
      console.error('Purchase error:', err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      intake: '#D4AF37',
      documents_pending: '#F59E0B',
      documents_received: '#10B981',
      under_review: '#3B82F6',
      review_complete: '#8B5CF6',
      consultation_scheduled: '#EC4899',
      consultation_complete: '#10B981',
      report_pending: '#F59E0B',
      report_delivered: '#10B981',
      closed: '#6B7280'
    };
    return colors[status] || '#6B7280';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Handle tab change with scroll to top
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Scroll the main content area to top
    const mainContent = document.querySelector('.portal-main');
    if (mainContent) {
      mainContent.scrollTo(0, 0);
    }
    // Also scroll window to top for mobile
    window.scrollTo(0, 0);
  };

  // NEW COPPER SATIN - user provided, high quality
  const copperBgStyle = {
    backgroundImage: `url('/copper-satin-new.png')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#8B4513',
    minHeight: '100vh',
    width: '100%'
  };

  if (loading || isLoading) {
    return (
      <div className="portal-loading" style={copperBgStyle}>
        <div className="loading-spinner"></div>
        <p>Loading your portal...</p>
      </div>
    );
  }

  return (
    <div className={`portal-page ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`} style={copperBgStyle}>
      {/* Collapse toggle on OUTSIDE of sidebar */}
      <button 
        className="sidebar-toggle-outside"
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        data-testid="sidebar-toggle"
      >
        {sidebarCollapsed ? <Menu size={24} /> : <ChevronLeft size={24} />}
      </button>

      {/* Sidebar - Smooth Nappa Leather (Satin Finish) */}
      <aside 
        className={`portal-sidebar ${sidebarCollapsed ? 'collapsed' : 'open'}`}
        style={{
          /* Base: Rich Cognac #944B1E with gradient for depth */
          background: `linear-gradient(
            135deg,
            #BD7A4E 0%,
            #944B1E 25%,
            #944B1E 50%,
            #7A3A15 75%,
            #5C2C0E 100%
          )`,
          /* Subtle light overlay - mimics curved surface */
          boxShadow: `
            inset 0 0 60px rgba(189, 122, 78, 0.15),
            20px 0 40px rgba(0, 0, 0, 0.3)
          `
        }}
      >
        <div className="sidebar-header">
          {/* Gold etched emblem at TOP of sidebar - same as login page - disappears when collapsed */}
          {!sidebarCollapsed && (
            <Link to="/" className="sidebar-logo-link">
              <img src="/cadence-logo-embossed.png" alt="CADENCE" className="sidebar-c-emblem" />
            </Link>
          )}
          {!sidebarCollapsed && (
            <Link to="/" className="back-to-site" data-testid="back-to-website">
              ← Back to Website
            </Link>
          )}
        </div>
        
        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleTabChange('dashboard')}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard size={20} />
            <span>Dashboard</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'welcome' ? 'active' : ''}`}
            onClick={() => handleTabChange('welcome')}
            data-testid="nav-welcome"
          >
            <Mail size={20} />
            <span>Welcome Center</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'intake' ? 'active' : ''}`}
            onClick={() => handleTabChange('intake')}
            data-testid="nav-intake"
          >
            <ClipboardList size={20} />
            <span>Health Intake Form</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => handleTabChange('documents')}
            data-testid="nav-documents"
          >
            <Upload size={20} />
            <span>Documents</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'purchase' ? 'active' : ''}`}
            onClick={() => handleTabChange('purchase')}
            data-testid="nav-purchase"
          >
            <ShoppingCart size={20} />
            <span>Purchase Services</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'cases' ? 'active' : ''}`}
            onClick={() => handleTabChange('cases')}
            data-testid="nav-cases"
          >
            <FileText size={20} />
            <span>My Cases</span>
            {dashboardData?.active_cases > 0 && (
              <span className="badge">{dashboardData.active_cases}</span>
            )}
          </button>
          <button 
            className={`nav-item ${activeTab === 'consultations' ? 'active' : ''}`}
            onClick={() => handleTabChange('consultations')}
            data-testid="nav-consultations"
          >
            <Calendar size={20} />
            <span>My Consultations</span>
            {dashboardData?.upcoming_consultations > 0 && (
              <span className="badge">{dashboardData.upcoming_consultations}</span>
            )}
          </button>
          <button 
            className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => handleTabChange('messages')}
            data-testid="nav-messages"
          >
            <MessageSquare size={20} />
            <span>Messages</span>
            {dashboardData?.unread_messages > 0 && (
              <span className="badge urgent">{dashboardData.unread_messages}</span>
            )}
          </button>
          <button 
            className={`nav-item ${activeTab === 'resources' ? 'active' : ''}`}
            onClick={() => handleTabChange('resources')}
            data-testid="nav-resources"
          >
            <BookOpen size={20} />
            <span>Medical Resources</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'affiliate' ? 'active' : ''}`}
            onClick={() => handleTabChange('affiliate')}
            data-testid="nav-affiliate"
          >
            <Users size={20} />
            <span>Affiliate Program</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => handleTabChange('profile')}
            data-testid="nav-profile"
          >
            <User size={20} />
            <span>My Profile</span>
          </button>
        </nav>

        {/* Admin Portal Link at BOTTOM of sidebar */}
        {user && ['admin', 'staff', 'reviewer', 'provider'].includes(user.role) && (
          <div className="admin-portal-link">
            <Link to="/admin" className="nav-item admin-link" data-testid="nav-admin">
              <Shield size={20} />
              <span>Admin Portal</span>
              <ChevronRight size={16} />
            </Link>
          </div>
        )}

        <div className="sidebar-footer">
          <div className="hipaa-badge">
            <Shield size={16} />
            <span>HIPAA Compliant</span>
          </div>
          {/* Collapse toggle at BOTTOM of sidebar */}
          <button 
            className="sidebar-collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            data-testid="sidebar-collapse-bottom"
          >
            {sidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
            {!sidebarCollapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="portal-main">
        {/* Header */}
        <header className="portal-header">
          <div className="header-welcome">
            <h1>{user?.first_name || 'Patient'}, Welcome to your CADENCE Portal!</h1>
          </div>
          <div className="header-right">
            {/* Admin Portal Button - only for admin users */}
            {user && ['admin', 'staff', 'reviewer', 'provider'].includes(user.role) && (
              <Link to="/admin" className="admin-header-btn" data-testid="header-admin-btn">
                <Shield size={18} />
                <span>Admin Portal</span>
              </Link>
            )}
            <button className="notification-btn">
              <Bell size={20} />
              {dashboardData?.unread_messages > 0 && (
                <span className="notification-dot"></span>
              )}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="portal-content">
          {activeTab === 'dashboard' && (
            <DashboardView 
              data={dashboardData} 
              cases={cases}
              user={user}
              onViewCase={(id) => { setActiveTab('cases'); }}
              onStartIntake={() => setActiveTab('intake')}
            />
          )}
          {activeTab === 'welcome' && (
            <WelcomeView user={user} />
          )}
          {activeTab === 'intake' && (
            <ComprehensiveIntakeView 
              token={token} 
              user={user}
              isGuest={isGuestMode}
              isAuthenticated={isAuthenticated}
              paymentSuccess={paymentSuccess}
              onComplete={() => {
                fetchCases();
                setPaymentSuccess(false);
                setActiveTab('cases');
              }} 
            />
          )}
          {activeTab === 'purchase' && (
            <PurchaseView onPurchase={handlePurchase} />
          )}
          {activeTab === 'cases' && (
            <CasesView 
              cases={cases} 
              getStatusColor={getStatusColor}
              formatStatus={formatStatus}
              token={token}
            />
          )}
          {activeTab === 'consultations' && (
            <ConsultationsView token={token} />
          )}
          {activeTab === 'messages' && (
            <MessagesView messages={messages} token={token} />
          )}
          {activeTab === 'documents' && (
            <DocumentsView token={token} cases={cases} />
          )}
          {activeTab === 'resources' && (
            <MedicalResourcesView />
          )}
          {activeTab === 'affiliate' && (
            <AffiliatePortalView user={user} />
          )}
          {activeTab === 'profile' && (
            <ProfileView user={user} token={token} />
          )}
        </div>
      </main>
    </div>
  );
};

// Medical Resources View Component (only for logged-in portal users)
const MedicalResourcesView = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeAnatomyCategory, setActiveAnatomyCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  
  // Number of free articles before requiring membership
  const FREE_ARTICLE_LIMIT = 2;

  const categories = [
    { id: 'all', name: 'All Resources' },
    { id: 'womens', name: "Women's Health & GYN" },
    { id: 'msk', name: 'Musculoskeletal' },
    { id: 'endocrine', name: 'Endocrine & Hormones' },
    { id: 'autoimmune', name: 'Autoimmune' },
    { id: 'cardiology', name: 'Cardiology' },
    { id: 'neurology', name: 'Neurology' },
    { id: 'gastro', name: 'Gastroenterology' }
  ];

  const anatomyCategories = [
    { id: 'all', name: 'All Diagrams' },
    { id: 'gyn', name: 'GYN / Uterine' },
    { id: 'msk', name: 'Musculoskeletal' },
    { id: 'endocrine', name: 'Endocrine' },
    { id: 'cardio', name: 'Cardiovascular' },
    { id: 'gastro', name: 'Digestive' },
    { id: 'neuro', name: 'Neurological' }
  ];

  // Real peer-reviewed articles with actual DOI links
  const articles = [
    // Women's Health & GYN
    {
      id: 1,
      title: "Endometriosis: Diagnosis and Management",
      category: 'womens',
      summary: "Endometriosis affects 10% of reproductive-age women. This review covers current understanding of pathophysiology, diagnostic approaches including imaging, and medical and surgical management options.",
      source: "JAMA",
      journal: "JAMA",
      year: "2025",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2821194",
      doi: "10.1001/jama.2024.21089"
    },
    {
      id: 2,
      title: "Polycystic Ovary Syndrome",
      category: 'womens',
      summary: "PCOS is the most common endocrine disorder in women. Learn about diagnostic criteria (oligoanovulation, hyperandrogenism, polycystic morphology), metabolic implications, and evidence-based treatment approaches.",
      source: "JAMA",
      journal: "JAMA",
      year: "2020",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2788139",
      doi: "10.1001/jama.2020.14763"
    },
    {
      id: 3,
      title: "Uterine Fibroids",
      category: 'womens',
      summary: "Uterine leiomyomas (fibroids) affect up to 70-80% of women. Review of FIGO classification, imaging diagnosis, symptoms, and treatment options including medical therapy, ablation, and surgical approaches.",
      source: "NEJM",
      journal: "New England Journal of Medicine",
      year: "2024",
      url: "https://www.nejm.org/doi/full/10.1056/NEJMcp2309235",
      doi: "10.1056/NEJMcp2309235"
    },
    {
      id: 4,
      title: "Menopause: Hormone Therapy",
      category: 'womens',
      summary: "Understanding menopausal hormone therapy risks and benefits. Evidence-based guidance on treatment decisions for vasomotor symptoms, bone health, and cardiovascular considerations.",
      source: "JAMA",
      journal: "JAMA",
      year: "2017",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2648632",
      doi: "10.1001/jama.2017.7938"
    },
    {
      id: 5,
      title: "Adenomyosis: An Update",
      category: 'womens',
      summary: "Adenomyosis causes heavy menstrual bleeding and pelvic pain. Current understanding of this condition affecting the uterine wall and its relationship to endometriosis.",
      source: "Obstetrics & Gynecology",
      journal: "Obstet Gynecol",
      year: "2022",
      url: "https://pubmed.ncbi.nlm.nih.gov/35852291/",
      doi: "10.1097/AOG.0000000000004891"
    },
    // Musculoskeletal
    {
      id: 6,
      title: "Hip and Knee Osteoarthritis",
      category: 'msk',
      summary: "Osteoarthritis is the most common joint disease, affecting over 240 million worldwide. Review covers pathophysiology, diagnosis via physical exam and radiographs, and treatments from lifestyle to joint replacement.",
      source: "JAMA",
      journal: "JAMA",
      year: "2021",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2776205",
      doi: "10.1001/jama.2020.22171"
    },
    {
      id: 7,
      title: "Low Back Pain",
      category: 'msk',
      summary: "Low back pain is a leading cause of disability worldwide. Evidence-based approach to diagnosis, imaging decisions, and treatment including physical therapy and medications.",
      source: "JAMA",
      journal: "JAMA",
      year: "2017",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2616395",
      doi: "10.1001/jama.2016.19029"
    },
    {
      id: 25,
      title: "Rotator Cuff Disorders",
      category: 'msk',
      summary: "Rotator cuff disorders include tendinopathy, partial tears, and full-thickness tears. Review of diagnostic evaluation, conservative management with PT, and surgical indications.",
      source: "JAMA",
      journal: "JAMA",
      year: "2020",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2760744",
      doi: "10.1001/jama.2019.21411"
    },
    {
      id: 26,
      title: "Carpal Tunnel Syndrome",
      category: 'msk',
      summary: "Carpal tunnel is the most common nerve entrapment. Diagnosis, conservative treatments including splinting and injections, and surgical decompression indications.",
      source: "JAMA",
      journal: "JAMA",
      year: "2019",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2720757",
      doi: "10.1001/jama.2019.0602"
    },
    {
      id: 8,
      title: "Fibromyalgia: Diagnosis and Management",
      category: 'autoimmune',
      summary: "Fibromyalgia is a chronic widespread pain condition. Review covers diagnostic criteria, central sensitization, multidisciplinary treatment approaches including medications and exercise.",
      source: "JAMA",
      journal: "JAMA",
      year: "2014",
      url: "https://jamanetwork.com/journals/jama/fullarticle/1860480",
      doi: "10.1001/jama.2014.3266"
    },
    {
      id: 9,
      title: "Neck Pain",
      category: 'msk',
      summary: "Neck pain is common and can be disabling. Evaluation of cervical radiculopathy, degenerative changes, and evidence-based treatments from physical therapy to interventional options.",
      source: "JAMA",
      journal: "JAMA",
      year: "2017",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2598577",
      doi: "10.1001/jama.2017.0306"
    },
    {
      id: 10,
      title: "Osteoporosis in Postmenopausal Women",
      category: 'womens',
      summary: "Estrogen decline after menopause accelerates bone loss. Screening recommendations, fracture risk assessment, and treatment options including bisphosphonates and newer agents.",
      source: "JAMA",
      journal: "JAMA",
      year: "2021",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2775299",
      doi: "10.1001/jama.2020.22298"
    },
    // Endocrine & Hormones
    {
      id: 11,
      title: "Hypothyroidism",
      category: 'endocrine',
      summary: "Hypothyroidism affects metabolism, energy, and multiple organ systems. Review covers TSH and T4 testing, levothyroxine dosing starting at 1.6 mcg/kg/day, and monitoring.",
      source: "JAMA",
      journal: "JAMA",
      year: "2025",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2843504",
      doi: "10.1001/jama.2025.13559"
    },
    {
      id: 12,
      title: "Type 2 Diabetes Mellitus",
      category: 'endocrine',
      summary: "Comprehensive review of T2DM affecting over 450 million worldwide. Covers screening, diagnosis, lifestyle modifications, and pharmacotherapy including newer agents.",
      source: "JAMA",
      journal: "JAMA",
      year: "2021",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2777080",
      doi: "10.1001/jama.2021.4788"
    },
    {
      id: 13,
      title: "Thyroid Nodules",
      category: 'endocrine',
      summary: "Thyroid nodules are common, found in up to 65% of adults on ultrasound. Review of evaluation, biopsy indications, and management of benign vs suspicious nodules.",
      source: "JAMA",
      journal: "JAMA",
      year: "2018",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2701481",
      doi: "10.1001/jama.2018.10853"
    },
    // Autoimmune
    {
      id: 15,
      title: "Systemic Lupus Erythematosus",
      category: 'autoimmune',
      summary: "SLE is a complex autoimmune disease affecting multiple organ systems primarily in women. Review of classification criteria, disease manifestations, and treatment including hydroxychloroquine and immunosuppressants.",
      source: "JAMA",
      journal: "JAMA",
      year: "2024",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2814854",
      doi: "10.1001/jama.2024.0525"
    },
    {
      id: 16,
      title: "Rheumatoid Arthritis",
      category: 'autoimmune',
      summary: "RA is a chronic inflammatory arthritis affecting 1% of adults. Modern treatment approaches including methotrexate, biologics, and the importance of treat-to-target strategies.",
      source: "JAMA",
      journal: "JAMA",
      year: "2018",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2719693",
      doi: "10.1001/jama.2018.13103"
    },
    {
      id: 17,
      title: "Psoriatic Arthritis",
      category: 'autoimmune',
      summary: "Psoriatic arthritis affects up to 30% of patients with psoriasis. Clinical features, diagnosis, and treatment including DMARDs and biologics targeting TNF and IL-17.",
      source: "JAMA",
      journal: "JAMA",
      year: "2019",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2730466",
      doi: "10.1001/jama.2019.2575"
    },
    // Cardiology
    {
      id: 18,
      title: "Heart Failure",
      category: 'cardiology',
      summary: "Heart failure affects over 6 million US adults. Classification (HFrEF vs HFpEF), diagnosis via NT-proBNP and echo, and guideline-directed medical therapy including SGLT2 inhibitors.",
      source: "JAMA",
      journal: "JAMA",
      year: "2021",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2776766",
      doi: "10.1001/jama.2020.26115"
    },
    {
      id: 19,
      title: "Atrial Fibrillation",
      category: 'cardiology',
      summary: "AFib is the most common sustained arrhythmia affecting 33 million worldwide. Stroke prevention with anticoagulation, rate vs rhythm control, and catheter ablation indications.",
      source: "JAMA",
      journal: "JAMA",
      year: "2020",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2764835",
      doi: "10.1001/jama.2020.2411"
    },
    // Neurology
    {
      id: 20,
      title: "Migraine",
      category: 'neurology',
      summary: "Migraine affects 15% of adults worldwide. Pathophysiology involving CGRP, acute treatment with triptans, and preventive therapies including new CGRP-targeting medications.",
      source: "JAMA",
      journal: "JAMA",
      year: "2021",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2778489",
      doi: "10.1001/jama.2021.1640"
    },
    {
      id: 21,
      title: "Alzheimer Disease",
      category: 'neurology',
      summary: "Alzheimer disease is the most common cause of dementia. Review of pathophysiology, clinical diagnosis, biomarkers, and current and emerging treatments.",
      source: "JAMA",
      journal: "JAMA",
      year: "2020",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2764046",
      doi: "10.1001/jama.2019.21458"
    },
    // Gastroenterology
    {
      id: 22,
      title: "Irritable Bowel Syndrome",
      category: 'gastro',
      summary: "IBS affects 10-15% of adults with abdominal pain and altered bowel habits. Rome IV criteria, dietary approaches including low FODMAP, and pharmacological management.",
      source: "JAMA",
      journal: "JAMA",
      year: "2015",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2470773",
      doi: "10.1001/jama.2015.13260"
    },
    {
      id: 23,
      title: "Inflammatory Bowel Disease",
      category: 'gastro',
      summary: "Crohn's disease and ulcerative colitis affect over 3 million US adults. Diagnosis, monitoring, and evolving treatment landscape including biologics and small molecules.",
      source: "JAMA",
      journal: "JAMA",
      year: "2021",
      url: "https://jamanetwork.com/journals/jama/fullarticle/2777081",
      doi: "10.1001/jama.2020.22791"
    }
  ];

  // Anatomical diagrams with plain language labels
  const anatomyResources = [
    // GYN / Uterine Anatomy & Pathology
    {
      id: 1,
      title: "Female Reproductive System - Labeled",
      image: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=800",
      caption: "The female reproductive system includes the ovaries, fallopian tubes, uterus, cervix, and vagina. The ovaries produce eggs and hormones. The uterus (womb) is where pregnancy develops.",
      labels: ["Ovaries - produce eggs & hormones", "Fallopian tubes - carry eggs to uterus", "Uterus (womb) - site of pregnancy", "Cervix - lower part of uterus", "Vagina - birth canal"],
      category: "gyn",
      diagramType: "anatomical"
    },
    {
      id: 2,
      title: "Uterine Fibroid Types & Locations",
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800",
      caption: "Fibroids (leiomyomas) are benign growths classified by location: SUBSEROSAL grow outward, INTRAMURAL are within the muscle wall, SUBMUCOSAL bulge into the cavity causing heavy bleeding, and PEDUNCULATED are attached by a stalk.",
      labels: ["Subserosal - outside wall, causes pressure", "Intramural - within wall, enlarges uterus", "Submucosal - inside cavity, causes heavy bleeding", "Pedunculated - attached by stalk", "Cervical fibroids - rare but possible"],
      category: "gyn",
      diagramType: "pathology"
    },
    {
      id: 3,
      title: "Endometriosis Sites",
      image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800",
      caption: "Endometriosis occurs when tissue similar to the uterine lining grows outside the uterus. Common sites include ovaries (forming 'chocolate cysts'), fallopian tubes, pelvic lining, and rarely bowel or bladder.",
      labels: ["Ovarian endometriomas (chocolate cysts)", "Fallopian tube involvement", "Pelvic peritoneum lesions", "Bowel/bladder involvement (rare)", "Deep infiltrating endometriosis"],
      category: "gyn",
      diagramType: "pathology"
    },
    {
      id: 4,
      title: "Uterine Positions - Normal Variations",
      image: "https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=800",
      caption: "The uterus can naturally tilt forward (anteverted) or backward (retroverted). About 20% of women have a retroverted uterus - this is usually normal and doesn't affect fertility or pregnancy.",
      labels: ["Anteverted - tilts forward (most common)", "Anteflexed - bends forward at body-cervix junction", "Retroverted - tilts backward (20% of women)", "Retroflexed - bends backward"],
      category: "gyn",
      diagramType: "anatomical"
    },
    {
      id: 5,
      title: "Adenomyosis - Understanding the Condition",
      image: "https://images.unsplash.com/photo-1584362917165-526a968a6a8c?w=800",
      caption: "Adenomyosis occurs when endometrial tissue grows INTO the muscular wall of the uterus, causing an enlarged, tender uterus with heavy, painful periods. IMPORTANT: Women with adenomyosis should generally AVOID combined hormonal contraceptives containing estrogen.",
      labels: ["Endometrial tissue invading muscle", "Thickened uterine wall", "Enlarged 'boggy' uterus", "⚠️ Avoid combined estrogen contraceptives", "Progestin-only or GnRH agonists preferred"],
      category: "gyn",
      diagramType: "pathology",
      warning: true
    },
    {
      id: 6,
      title: "Polycystic Ovarian Syndrome (PCOS)",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800",
      caption: "PCOS is characterized by multiple small follicles on the ovaries that fail to mature, elevated androgens (testosterone), and irregular ovulation. It's a metabolic and hormonal syndrome, not just about 'cysts'.",
      labels: ["Multiple immature follicles", "Elevated androgens (testosterone)", "Insulin resistance common", "Irregular or absent ovulation", "Associated with hirsutism & acne"],
      category: "gyn",
      diagramType: "pathology"
    },
    // Musculoskeletal
    {
      id: 7,
      title: "The Spine - Vertebral Regions",
      image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800",
      caption: "Your spine has natural curves that distribute weight and absorb shock. The lumbar (lower back) region bears the most weight and is a common source of pain.",
      labels: ["Cervical (neck) - 7 vertebrae", "Thoracic (mid-back) - 12 vertebrae", "Lumbar (lower back) - 5 vertebrae", "Sacrum - 5 fused vertebrae", "Coccyx (tailbone) - 3-5 fused"],
      category: "msk",
      diagramType: "anatomical"
    },
    {
      id: 8,
      title: "The Knee Joint",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800",
      caption: "The knee is the largest joint in your body. It's stabilized by ligaments (ACL, PCL, MCL, LCL) and cushioned by cartilage (meniscus).",
      labels: ["Femur (thigh bone)", "Tibia (shin bone)", "Patella (kneecap)", "Meniscus (cartilage cushion)", "ACL & PCL (cruciate ligaments)", "MCL & LCL (collateral ligaments)"],
      category: "msk",
      diagramType: "anatomical"
    },
    {
      id: 9,
      title: "The Shoulder - Rotator Cuff",
      image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800",
      caption: "The rotator cuff is a group of four muscles and tendons that stabilize your shoulder. Tears or inflammation cause pain with overhead movements.",
      labels: ["Supraspinatus - abduction", "Infraspinatus - external rotation", "Teres minor - external rotation", "Subscapularis - internal rotation", "Glenohumeral joint", "Acromion"],
      category: "msk",
      diagramType: "anatomical"
    },
    {
      id: 10,
      title: "Hip Joint Anatomy",
      image: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=800",
      caption: "The hip is a ball-and-socket joint where the femur meets the pelvis. The labrum and surrounding muscles provide stability. Hip pain can radiate to the groin or thigh.",
      labels: ["Acetabulum (socket)", "Femoral head (ball)", "Labrum (cartilage ring)", "Hip capsule & ligaments", "Greater trochanter"],
      category: "msk",
      diagramType: "anatomical"
    },
    // Endocrine & Hormonal
    {
      id: 11,
      title: "The Pituitary-Thyroid-Ovarian Axis",
      image: "https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?w=800",
      caption: "The pituitary gland in your brain controls the thyroid and ovaries through a feedback loop. Disruption at any level can cause hormonal imbalance.",
      labels: ["Hypothalamus - releases GnRH", "Pituitary - releases LH, FSH, TSH", "Thyroid - produces T3, T4", "Ovaries - produce estrogen, progesterone", "Feedback loops regulate hormones"],
      category: "endocrine",
      diagramType: "system"
    },
    {
      id: 12,
      title: "The Thyroid Gland",
      image: "https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=800",
      caption: "Your thyroid is a butterfly-shaped gland in your neck that controls metabolism, energy levels, and body temperature through hormones T3 and T4.",
      labels: ["Thyroid gland location", "Right & left lobes", "Isthmus (connecting bridge)", "Trachea (windpipe)", "Thyroid hormones T3 & T4"],
      category: "endocrine",
      diagramType: "anatomical"
    },
    {
      id: 13,
      title: "The Adrenal Glands",
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800",
      caption: "Your adrenal glands sit atop your kidneys and produce cortisol (stress hormone), aldosterone (blood pressure), and androgens (sex hormones like DHEA-S).",
      labels: ["Adrenal cortex - outer layer", "Adrenal medulla - inner layer", "Cortisol production", "Aldosterone production", "DHEA-S & androgens"],
      category: "endocrine",
      diagramType: "anatomical"
    },
    // Cardiovascular & Digestive
    {
      id: 14,
      title: "The Heart - Blood Flow",
      image: "https://images.unsplash.com/photo-1628348068343-c6a848d2b6dd?w=800",
      caption: "Your heart pumps blood through four chambers. The right side sends blood to your lungs; the left side pumps oxygenated blood to your body.",
      labels: ["Right atrium - receives deoxygenated blood", "Right ventricle - pumps to lungs", "Left atrium - receives oxygenated blood", "Left ventricle - pumps to body", "Aorta - main artery"],
      category: "cardio",
      diagramType: "anatomical"
    },
    {
      id: 15,
      title: "The Digestive System",
      image: "https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=800",
      caption: "Food travels through a 30-foot journey from mouth to rectum. Different organs break down and absorb nutrients along the way.",
      labels: ["Esophagus", "Stomach", "Small intestine - nutrient absorption", "Large intestine - water absorption", "Liver, Gallbladder & Pancreas - digestive enzymes"],
      category: "gastro",
      diagramType: "system"
    }
  ];

  const filteredArticles = activeCategory === 'all' 
    ? articles 
    : articles.filter(a => a.category === activeCategory);

  const filteredAnatomy = activeAnatomyCategory === 'all'
    ? anatomyResources
    : anatomyResources.filter(a => a.category === activeAnatomyCategory);

  return (
    <div className="resources-view" data-testid="resources-view">
      {/* Header */}
      <div className="resources-header">
        <img src="/cadence-emblem-velvet.png" alt="CADENCE" style={{ width: '60px' }} />
        <div className="resources-header-content">
          <h2>Medical Resources Library</h2>
          <p>Peer-reviewed articles and anatomical guides to help you understand your health</p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="resources-disclaimer">
        <Info size={20} />
        <p>
          <strong>For Educational Purposes:</strong> These resources are from peer-reviewed medical journals to help you understand health conditions. 
          They do not replace professional medical advice. Always discuss your specific situation with your healthcare provider.
        </p>
      </div>

      {/* Category Filter */}
      <div className="resources-categories">
        {categories.map(cat => (
          <button
            key={cat.id}
            className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Articles Grid */}
      <div className="articles-grid">
        {filteredArticles.map((article, index) => {
          const isLocked = index >= FREE_ARTICLE_LIMIT;
          
          if (isLocked) {
            return (
              <div 
                key={article.id}
                className="article-card-link locked"
                onClick={() => setShowMembershipModal(true)}
                data-testid={`article-${article.id}-locked`}
              >
                <div className="article-card locked-article">
                  <div 
                    className="article-image locked-overlay" 
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(74, 14, 78, 0.15) 0%, rgba(212, 175, 55, 0.15) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <div className="lock-badge">
                      <Lock size={28} color="#D4AF37" style={{ opacity: 0.6 }} />
                    </div>
                  </div>
                  <div className="article-content blurred">
                    <span className="article-category">{categories.find(c => c.id === article.category)?.name || article.category}</span>
                    <h3>{article.title}</h3>
                    <p className="blurred-text">{article.summary.substring(0, 80)}...</p>
                    <div className="members-only-badge">
                      <Lock size={14} />
                      <span>Members Only</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          
          return (
            <a 
              key={article.id} 
              href={article.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="article-card-link"
              data-testid={`article-${article.id}`}
            >
              <div className="article-card">
                <div 
                  className="article-image" 
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(74, 14, 78, 0.1) 0%, rgba(212, 175, 55, 0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <BookOpen size={48} color="#4A0E4E" style={{ opacity: 0.3 }} />
                </div>
                <div className="article-content">
                  <span className="article-category">{categories.find(c => c.id === article.category)?.name || article.category}</span>
                  <h3>{article.title}</h3>
                  <p>{article.summary}</p>
                  <div className="article-meta">
                    <span className="article-source">
                      <strong>{article.source}</strong> • {article.journal}, {article.year}
                    </span>
                    <span className="article-link">
                      Read Article <ExternalLink size={14} />
                    </span>
                  </div>
                </div>
              </div>
            </a>
          );
        })}
      </div>
      
      {/* Membership Modal */}
      {showMembershipModal && (
        <div className="modal-overlay" onClick={() => setShowMembershipModal(false)}>
          <div className="modal membership-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowMembershipModal(false)}>
              <X size={24} />
            </button>
            <div className="membership-modal-content">
              <div className="membership-crest">
                <img 
                  src="/cadence-emblem-velvet.png" 
                  alt="CADENCE" 
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <h2>Members Only Content</h2>
              <p>
                Unlock full access to our comprehensive medical resources library, 
                including expert-curated articles, anatomy guides, and educational materials.
              </p>
              <div className="membership-benefits">
                <h4>Membership Benefits:</h4>
                <ul>
                  <li><CheckCircle size={16} /> Full access to all medical articles</li>
                  <li><CheckCircle size={16} /> Detailed anatomy diagrams & guides</li>
                  <li><CheckCircle size={16} /> Priority scheduling support</li>
                  <li><CheckCircle size={16} /> Direct messaging with care team</li>
                </ul>
              </div>
              <div className="membership-cta">
                <a href="/membership" className="btn-primary subscribe-btn">
                  Subscribe Now
                </a>
                <a href="/pricing" className="btn-secondary view-plans-btn">
                  View All Plans
                </a>
                <button className="btn-text" onClick={() => setShowMembershipModal(false)}>
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Anatomy Section */}
      <div className="anatomy-section">
        <h3><Stethoscope size={24} /> Understanding Your Body</h3>
        <p>Visual guides with plain-language explanations of how your body systems work, including uterine pathologies and musculoskeletal anatomy. <strong>Click any diagram to enlarge.</strong></p>
        
        {/* Anatomy Category Filter */}
        <div className="anatomy-categories">
          {anatomyCategories.map(cat => (
            <button
              key={cat.id}
              className={`anatomy-category-btn ${activeAnatomyCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveAnatomyCategory(cat.id)}
            >
              {cat.name}
            </button>
          ))}
        </div>
        
        <div className="anatomy-grid">
          {filteredAnatomy.map(resource => (
            <div key={resource.id} className={`anatomy-card ${resource.warning ? 'anatomy-card-warning' : ''}`}>
              <div 
                className="anatomy-image-container clickable"
                onClick={() => setSelectedImage(resource)}
                title="Click to enlarge"
              >
                <img 
                  src={resource.image} 
                  alt={resource.title}
                  className="anatomy-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML = `<div style="height: 180px; display: flex; align-items: center; justify-content: center; background: #F3F4F6; cursor: pointer;"><span style="color: #9CA3AF;">Diagram: ${resource.title}</span></div>`;
                  }}
                />
                <div className="zoom-hint">
                  <ZoomIn size={20} />
                </div>
              </div>
              <div className="anatomy-caption">
                <h4>{resource.title}</h4>
                <p className={resource.warning ? 'warning-text' : ''}>{resource.caption}</p>
                <div className="anatomy-labels">
                  {resource.labels.map((label, idx) => (
                    <span key={idx} className={`anatomy-label ${label.includes('⚠️') ? 'warning-label' : ''}`}>{label}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setSelectedImage(null)}>
              <X size={24} />
            </button>
            <img 
              src={selectedImage.image} 
              alt={selectedImage.title}
              className="modal-image"
            />
            <div className="modal-caption">
              <h3>{selectedImage.title}</h3>
              <p className={selectedImage.warning ? 'warning-text' : ''}>{selectedImage.caption}</p>
              <div className="modal-labels">
                {selectedImage.labels.map((label, idx) => (
                  <span key={idx} className={`anatomy-label ${label.includes('⚠️') ? 'warning-label' : ''}`}>{label}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Resources Note */}
      <div style={{ 
        background: 'rgba(212, 175, 55, 0.1)', 
        padding: '1.5rem', 
        borderRadius: '0.75rem',
        marginTop: '2rem',
        textAlign: 'center'
      }}>
        <h4 style={{ color: '#4A0E4E', marginBottom: '0.5rem' }}>Need More Resources?</h4>
        <p style={{ color: '#6B7280', marginBottom: '1rem' }}>
          During your consultation, we can provide additional educational materials specific to your health concerns.
        </p>
        <Link to="/contact" className="btn-gold" style={{ display: 'inline-block' }}>
          Schedule a Consultation
        </Link>
      </div>
    </div>
  );
};

// ============================================================
// INTAKE PROGRESS — derive completion of the 8-section survey
// from the locally-saved draft so the dashboard can nudge
// patients to resume an in-progress intake.
// ============================================================
const fieldFilled = (v) =>
  typeof v === 'string' ? v.trim().length > 0 : Array.isArray(v) ? v.length > 0 : !!v;

const INTAKE_SECTION_CHECKS = [
  { title: 'Primary Concerns', done: (f) =>
      fieldFilled(f.primary_health_concern) ||
      [f.symptoms_fatigue, f.symptoms_pain, f.symptoms_digestive, f.symptoms_hormonal,
       f.symptoms_neurological, f.symptoms_cardiovascular, f.symptoms_respiratory, f.symptoms_skin]
        .some(fieldFilled) },
  { title: 'Medical History', done: (f) =>
      [f.current_diagnoses, f.past_diagnoses, f.surgical_history, f.hospitalizations].some(fieldFilled) },
  { title: 'Medications', done: (f) =>
      fieldFilled(f.current_prescriptions?.[0]?.medication) ||
      fieldFilled(f.current_supplements?.[0]?.supplement) ||
      fieldFilled(f.past_medications) },
  { title: 'Family History', done: (f) =>
      (f.family_history || []).some((m) => fieldFilled(m?.conditions)) ||
      [f.autoimmune_conditions, f.mental_health_family, f.addiction_history].some(fieldFilled) },
  { title: 'Lifestyle', done: (f) =>
      [f.diet_description, f.sleep_hours, f.sleep_quality, f.stress_level, f.exercise_routine].some(fieldFilled) },
  { title: 'Review of Systems', done: (f) =>
      [f.constitutional, f.heent, f.endocrine, f.gi, f.neuropsych, f.other_symptoms].some(fieldFilled) },
  { title: 'Your Perspective', done: (f) =>
      [f.suspected_cause, f.health_fears, f.diagnosis_concerns, f.emotional_impact].some(fieldFilled) },
  { title: 'Authorization', done: (f) => !!(f.consent_given && fieldFilled(f.signature)) },
];

const computeIntakeProgress = (formData) => {
  const total = INTAKE_SECTION_CHECKS.length;
  if (!formData) return { completed: 0, total, percent: 0 };
  const completed = INTAKE_SECTION_CHECKS.reduce((n, s) => n + (s.done(formData) ? 1 : 0), 0);
  return { completed, total, percent: Math.round((completed / total) * 100) };
};

// Dashboard View Component
const DashboardView = ({ data, cases, user, onViewCase, onStartIntake }) => {
  // Read the in-progress intake draft (same store the form auto-saves to).
  const intakeDraft = loadIntakeDraft();
  const intakeProgress = computeIntakeProgress(intakeDraft?.formData);
  const showIntakeProgress = intakeProgress.completed > 0 && intakeProgress.percent < 100;
  return (
    <div className="dashboard-view" data-testid="dashboard-view">
      {/* LARGE Gold Plate floating on copper */}
      <div className="dashboard-gold-plate-floating">
        <img src="/cadence-gold-plate.png" alt="CADENCE Medical Consulting" className="floating-gold-plate" />
      </div>
      
      {/* Floating Gold Quote - DIRECTLY ON SATIN - TWO LINES */}
      <div className="floating-gold-quote">
        <p>From chaos to clarity.<br/>From confusion to confidence.</p>
      </div>

      {/* Start Survey CTA with journey quote INSIDE */}
      <div className="start-survey-cta">
        <button className="start-survey-btn-gold" onClick={onStartIntake} data-testid="start-survey-main">
          <ClipboardList size={40} />
          <div className="survey-btn-content">
            <span className="survey-btn-title">START YOUR HEALTH SURVEY NOW</span>
            <span className="survey-btn-subtitle">Your healthcare advocacy journey continues here.</span>
          </div>
          <ChevronRight size={32} />
        </button>
      </div>

      {/* Intake progress meter — only shown when a draft is in progress, so it
          reads as a gentle "pick up where you left off" nudge. */}
      {showIntakeProgress && (
        <div className="intake-progress-card" data-testid="intake-progress-card">
          <div className="intake-progress-header">
            <ClipboardList size={22} />
            <div className="intake-progress-titles">
              <span className="intake-progress-title">Your Health Intake</span>
              <span className="intake-progress-sub" data-testid="intake-progress-sections">
                {intakeProgress.completed} of {intakeProgress.total} sections complete
              </span>
            </div>
            <span className="intake-progress-pct" data-testid="intake-progress-pct">{intakeProgress.percent}%</span>
          </div>
          <div className="intake-progress-track">
            <div className="intake-progress-fill" style={{ width: `${intakeProgress.percent}%` }} />
          </div>
          <button className="intake-progress-resume" onClick={onStartIntake} data-testid="resume-intake-btn">
            Resume Intake
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* Stats Grid - GOLD metallic tiles with embossed effect */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <FileText size={28} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{data?.active_cases || 0}</span>
            <span className="stat-label">Active Cases</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Upload size={28} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{data?.pending_documents || 0}</span>
            <span className="stat-label">Pending Documents</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <MessageSquare size={28} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{data?.unread_messages || 0}</span>
            <span className="stat-label">Unread Messages</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Calendar size={28} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{data?.upcoming_consultations || 0}</span>
            <span className="stat-label">Upcoming Consultations</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button className="action-card" onClick={onStartIntake} data-testid="start-intake-btn">
            <ClipboardList size={24} />
            <span>Complete Health Intake</span>
          </button>
          <Link to="#" className="action-card" onClick={() => onViewCase()}>
            <Activity size={24} />
            <span>View My Cases</span>
          </Link>
          <Link to="#" className="action-card">
            <Upload size={24} />
            <span>Upload Documents</span>
          </Link>
          <Link to="#" className="action-card">
            <MessageSquare size={24} />
            <span>Send Message</span>
          </Link>
        </div>
      </div>

      {/* Recent Cases */}
      <div className="recent-section">
        <h3>Recent Cases</h3>
        {cases.length > 0 ? (
          <div className="cases-list">
            {cases.slice(0, 3).map(caseItem => (
              <div key={caseItem.id} className="case-card-mini">
                <div className="case-header">
                  <span className="case-number">{caseItem.case_number}</span>
                  <span 
                    className="case-status"
                    style={{ background: `${caseItem.status === 'closed' ? '#6B7280' : '#D4AF37'}20`, 
                             color: caseItem.status === 'closed' ? '#6B7280' : '#D4AF37' }}
                  >
                    {caseItem.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="case-type">{caseItem.service_type}</p>
                <p className="case-date">
                  Created: {new Date(caseItem.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FileText size={48} />
            <p>No cases yet. Complete your health intake to get started.</p>
            <button className="btn-primary" onClick={onStartIntake} data-testid="empty-start-intake">
              Start Health Intake
            </button>
          </div>
        )}
      </div>

      {/* Large Gold C Stamp at BOTTOM - Centered on satin - CLICKABLE */}
      <div className="dashboard-bottom-foil">
        <Link to="/" className="dashboard-foil-link">
          <img src="/cadence-gold-foil.png" alt="CADENCE - Return to Home" className="dashboard-foil-emblem" />
        </Link>
      </div>
      
      {/* Bottom Quote - Gold Foiled on Satin - 2 LINES */}
      <div className="bottom-satin-quote">
        <p>
          <span className="quote-line-1">Clarity when you need it most.</span>
          <span className="quote-line-2">Advocacy when it matters most.</span>
        </p>
      </div>
      
      {/* Acronym Footer — 3 clean lines, no orphans on mobile */}
      <div className="dashboard-acronym-footer">
        <p>
          <span>Clinical Advocacy</span>
          <span>Decisive Empowerment</span>
          <span>Navigating Care &amp; Expertise</span>
        </p>
      </div>

      {/* Install App pill — same discreet treatment as the public Coming Soon page */}
      <InstallAppSection />
    </div>
  );
};

// Welcome Center View Component
const WelcomeView = ({ user }) => {
  return (
    <div className="welcome-view" data-testid="welcome-view">
      {/* Welcome Letter */}
      <div className="welcome-letter">
        <div className="letter-header">
          <img src="/cadence-emblem-velvet.png" alt="CADENCE Medical Consulting" className="letter-crest" />
          <div className="letter-title">
            <h2>Welcome to CADENCE</h2>
            <p className="letter-date">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
        </div>
        
        <div className="letter-body">
          <p className="greeting">Dear {user?.first_name || 'Valued Client'},</p>
          
          <p>Thank you for choosing CADENCE. Your comprehensive second opinion includes:</p>
          
          <ol className="welcome-list">
            <li><strong>Deep analysis of your medical records</strong></li>
            <li><strong>Personalized report with actionable insights</strong></li>
            <li><strong>60-minute consultation to discuss findings</strong></li>
          </ol>
          
          <div className="next-steps-box">
            <h4>Next Steps:</h4>
            <ol>
              <li>Complete your intake form (link below)</li>
              <li>Upload your medical records</li>
              <li>We&apos;ll contact you to schedule your consultation</li>
            </ol>
          </div>
          
          <p>We&apos;re honored to be your advocate.</p>
          
          <div className="letter-signature">
            <p>Sincerely,</p>
            <p className="signature-name">The CADENCE Team</p>
          </div>
        </div>
      </div>

      {/* Report Ready Notification (conditional) */}
      <div className="welcome-letter report-letter">
        <div className="letter-header">
          <img src="/cadence-emblem-velvet.png" alt="CADENCE" className="letter-crest" />
          <div className="letter-title">
            <h2>Your CADENCE Report</h2>
            <p className="letter-subtitle">When Your Report Is Ready</p>
          </div>
        </div>
        
        <div className="letter-body">
          <p className="greeting">Dear {user?.first_name || 'Client'},</p>
          
          <p>Your comprehensive analysis is complete and available in your client portal.</p>
          
          <p>Please review the report before our consultation on <strong>[Date]</strong>.</p>
          
          <div className="portal-link-box">
            <Link to="/portal" className="btn-gold">Access Your Portal</Link>
          </div>
          
          <p>We look forward to discussing your path forward.</p>
          
          <div className="letter-signature">
            <p>Best,</p>
            <p className="signature-name">[Your Name], DNP</p>
          </div>
        </div>
      </div>

      {/* Resources Section */}
      <div className="welcome-resources">
        <h3>Helpful Resources</h3>
        <div className="resources-grid">
          <div className="resource-card">
            <ClipboardList size={32} />
            <h4>Health Intake Form</h4>
            <p>Complete your comprehensive health history to help us understand your full picture.</p>
            <Link to="/portal" className="btn-outline" onClick={() => {}}>Start Intake</Link>
          </div>
          <div className="resource-card">
            <Upload size={32} />
            <h4>Document Upload</h4>
            <p>Upload your medical records, lab results, and imaging reports securely.</p>
            <Link to="/portal" className="btn-outline">Upload Files</Link>
          </div>
          <div className="resource-card">
            <Calendar size={32} />
            <h4>Schedule Consultation</h4>
            <p>Book your one-on-one consultation with our medical team.</p>
            <Link to="/contact" className="btn-outline">Schedule Now</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cases View Component
const CasesView = ({ cases, getStatusColor, formatStatus, token }) => {
  return (
    <div className="cases-view">
      {cases.length > 0 ? (
        <div className="cases-grid">
          {cases.map(caseItem => (
            <div key={caseItem.id} className="case-card">
              <div className="case-card-header">
                <h3>{caseItem.case_number}</h3>
                <span 
                  className="status-badge"
                  style={{ 
                    background: `${getStatusColor(caseItem.status)}20`,
                    color: getStatusColor(caseItem.status)
                  }}
                >
                  {formatStatus(caseItem.status)}
                </span>
              </div>
              <div className="case-card-body">
                <div className="case-detail">
                  <span className="label">Service</span>
                  <span className="value">{caseItem.service_type}</span>
                </div>
                <div className="case-detail">
                  <span className="label">Priority</span>
                  <span className={`value priority-${caseItem.priority}`}>
                    {caseItem.priority}
                  </span>
                </div>
                <div className="case-detail">
                  <span className="label">Chief Complaint</span>
                  <span className="value">{caseItem.chief_complaint?.substring(0, 100)}...</span>
                </div>
                <div className="case-detail">
                  <span className="label">Created</span>
                  <span className="value">
                    {new Date(caseItem.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="case-card-footer">
                <button className="btn-outline">View Details</button>
                <button className="btn-outline">
                  <MessageSquare size={16} />
                  Message
                </button>
                <ClinicalReportButton caseItem={caseItem} token={token} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <FileText size={64} />
          <h3>No Cases Yet</h3>
          <p>Start a new consultation to begin your healthcare advocacy journey.</p>
          <button className="btn-primary">Start New Case</button>
        </div>
      )}
    </div>
  );
};

// Consultations View Component - Shows upcoming and past video consultations
const ConsultationsView = ({ token }) => {
  const [consultations, setConsultations] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const calendarButtonRef = useRef(null);

  // Load Google Calendar script
  useEffect(() => {
    // Add the CSS link
    const link = document.createElement('link');
    link.href = 'https://calendar.google.com/calendar/scheduling-button-script.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Add the script
    const script = document.createElement('script');
    script.src = 'https://calendar.google.com/calendar/scheduling-button-script.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup
      if (document.head.contains(link)) document.head.removeChild(link);
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  // Initialize Google Calendar button
  useEffect(() => {
    let isInitialized = false;
    
    const initCalendarButton = () => {
      const target = document.getElementById('discovery-call-calendar');
      if (target && window.calendar && window.calendar.schedulingButton && !isInitialized) {
        target.innerHTML = ''; // Clear any existing content
        window.calendar.schedulingButton.load({
          url: 'https://calendar.google.com/calendar/appointments/AcZssZ2NT0EQeJwXBtgt467Nf8tknBTdgcFDOfy2_M0=?gv=true',
          color: '#D4AF37',
          label: 'Schedule Discovery Call',
          target: target,
        });
        isInitialized = true;
      }
    };

    // Try to initialize with delays to ensure script is loaded
    const timeout1 = setTimeout(initCalendarButton, 500);
    const timeout2 = setTimeout(initCalendarButton, 1500);
    const timeout3 = setTimeout(initCalendarButton, 3000);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [loading]);

  useEffect(() => {
    fetchConsultations();
  }, [token]);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/portal/consultations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConsultations(data);
      } else {
        throw new Error('Failed to fetch consultations');
      }
    } catch (err) {
      console.error('Consultations fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    };
  };

  const isWithin30Minutes = (dateStr) => {
    if (!dateStr) return false;
    const now = new Date();
    const meetingTime = new Date(dateStr);
    const diffMs = meetingTime - now;
    const diffMins = diffMs / (1000 * 60);
    return diffMins <= 30 && diffMins >= -60; // 30 min before to 60 min after start
  };

  const formatServiceType = (type) => {
    const types = {
      'diagnostic-review': 'Diagnostic Review',
      'advocacy-launch': 'Advocacy Launch',
      'deep-dive': 'Deep Dive',
      'follow-up': 'Follow-up Call'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="consultations-view">
        <div className="loading-state">
          <Clock size={48} className="spinning" />
          <p>Loading your consultations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="consultations-view">
        <div className="error-state">
          <AlertCircle size={48} />
          <h3>Unable to Load Consultations</h3>
          <p>{error}</p>
          <button onClick={fetchConsultations} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="consultations-view" data-testid="consultations-view">
      {/* Schedule Discovery Call - Premium Card */}
      <section className="discovery-call-section">
        <div className="discovery-call-card-portal">
          <div className="discovery-card-content">
            <div className="discovery-icon">
              <CalendarClock size={32} />
            </div>
            <div className="discovery-text">
              <h3>Schedule a Discovery Call</h3>
              <p>Book a complimentary call to discuss your healthcare needs and how we can help.</p>
            </div>
          </div>
          <div id="discovery-call-calendar" className="discovery-calendar-btn" data-testid="discovery-call-calendar"></div>
        </div>
      </section>

      {/* Upcoming Consultations */}
      <section className="consultations-section">
        <h2 className="section-title">
          <CalendarClock size={24} />
          Upcoming Consultations
        </h2>
        
        {consultations.upcoming && consultations.upcoming.length > 0 ? (
          <div className="consultations-grid">
            {consultations.upcoming.map((consultation, idx) => {
              const { date, time } = formatDateTime(consultation.scheduled_date);
              const isJoinable = isWithin30Minutes(consultation.scheduled_date);
              
              return (
                <div 
                  key={consultation.id || idx} 
                  className={`consultation-card upcoming ${isJoinable ? 'joinable' : ''}`}
                  data-testid={`consultation-card-${idx}`}
                >
                  <div className="consultation-header">
                    <span className={`status-badge ${consultation.status}`}>
                      {consultation.status === 'scheduled' ? 'Scheduled' : consultation.status}
                    </span>
                    {isJoinable && (
                      <span className="live-badge">
                        <span className="pulse"></span>
                        Ready to Join
                      </span>
                    )}
                  </div>
                  
                  <div className="consultation-details">
                    <h3 className="service-type">
                      {formatServiceType(consultation.service_type)}
                    </h3>
                    
                    <div className="datetime-info">
                      <div className="date">
                        <Calendar size={16} />
                        <span>{date}</span>
                      </div>
                      <div className="time">
                        <Clock size={16} />
                        <span>{time}</span>
                        <span className="duration">({consultation.duration_minutes || 60} min)</span>
                      </div>
                    </div>
                  </div>
                  
                  {consultation.meeting_link ? (
                    <a 
                      href={consultation.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`join-meeting-btn ${isJoinable ? 'active' : ''}`}
                      data-testid={`join-meeting-${idx}`}
                    >
                      <Video size={20} />
                      {isJoinable ? 'Join Meeting Now' : 'Join Meeting'}
                    </a>
                  ) : (
                    <div className="meeting-pending">
                      <Info size={16} />
                      <span>Meeting link will be sent before your appointment</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-consultations">
            <Calendar size={48} />
            <h3>No Upcoming Consultations</h3>
            <p>When you schedule a consultation, it will appear here with your meeting link.</p>
          </div>
        )}
      </section>
      
      {/* Past Consultations */}
      {consultations.past && consultations.past.length > 0 && (
        <section className="consultations-section past-section">
          <h2 className="section-title">
            <Clock size={24} />
            Past Consultations
          </h2>
          
          <div className="consultations-grid past">
            {consultations.past.slice(0, 5).map((consultation, idx) => {
              const { date, time } = formatDateTime(consultation.scheduled_date);
              
              return (
                <div 
                  key={consultation.id || `past-${idx}`} 
                  className="consultation-card past"
                  data-testid={`past-consultation-${idx}`}
                >
                  <div className="consultation-header">
                    <span className={`status-badge ${consultation.status}`}>
                      {consultation.status === 'completed' ? 'Completed' : consultation.status}
                    </span>
                  </div>
                  
                  <div className="consultation-details">
                    <h3 className="service-type">
                      {formatServiceType(consultation.service_type)}
                    </h3>
                    
                    <div className="datetime-info">
                      <div className="date">
                        <Calendar size={16} />
                        <span>{date}</span>
                      </div>
                      <div className="time">
                        <Clock size={16} />
                        <span>{time}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

// Affiliate Portal View Component - Embedded affiliate program in patient portal
const AffiliatePortalView = ({ user }) => {
  const [affiliateCode, setAffiliateCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(`https://cadencemedicalconsulting.com/?ref=${affiliateCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApply = () => {
    // Generate affiliate code based on user name
    const code = 'CADENCE' + (user?.first_name?.substring(0,3) || 'REF').toUpperCase() + Math.random().toString(36).substring(2, 6).toUpperCase();
    setAffiliateCode(code);
    setHasApplied(true);
  };

  const commissionData = [
    { service: 'Diagnostic Bundle Review', price: '$495', commission: '$49.50', recurring: false },
    { service: 'Advocacy Launch (30-Min)', price: '$1,197', commission: '$119.70', recurring: false },
    { service: 'Deep Dive Consultation', price: '$1,797', commission: '$179.70', recurring: false },
    { service: 'Navigator Membership', price: '$179/mo', commission: '$17.90/month', recurring: true },
    { service: 'Advocate Membership', price: '$399/mo', commission: '$39.90/month', recurring: true },
    { service: 'Concierge Membership', price: '$799/mo', commission: '$79.90/month', recurring: true },
  ];

  return (
    <div className="affiliate-portal-view" data-testid="affiliate-portal-view">
      {/* Welcome Section */}
      <div className="affiliate-welcome-card">
        <div className="affiliate-welcome-content">
          <Gift size={48} className="affiliate-icon" />
          <div>
            <h2>Earn with CADENCE Affiliate Program</h2>
            <p>Earn 10% commission on every referral. Help others access quality medical advocacy while building passive income.</p>
          </div>
        </div>
      </div>

      {/* Your Affiliate Status */}
      {!hasApplied ? (
        <div className="affiliate-apply-card">
          <h3>Join Our Affiliate Program</h3>
          <p>Ready to start earning? Click below to activate your affiliate account.</p>
          <div className="affiliate-benefits-mini">
            <div className="benefit-mini">
              <DollarSign size={20} />
              <span>10% Commission</span>
            </div>
            <div className="benefit-mini">
              <TrendingUp size={20} />
              <span>Recurring Income</span>
            </div>
            <div className="benefit-mini">
              <Users size={20} />
              <span>Help Others</span>
            </div>
          </div>
          <button className="btn-primary" onClick={handleApply} data-testid="affiliate-apply-btn">
            Activate Affiliate Account
          </button>
        </div>
      ) : (
        <div className="affiliate-status-card">
          <h3>Your Affiliate Dashboard</h3>
          <div className="affiliate-link-box">
            <label>Your Referral Link</label>
            <div className="affiliate-link-row">
              <input 
                type="text" 
                value={`https://cadencemedicalconsulting.com/?ref=${affiliateCode}`}
                readOnly 
              />
              <button onClick={handleCopy} className="copy-btn" data-testid="copy-affiliate-link">
                {copied ? <CheckCircle size={20} /> : <Copy size={20} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="affiliate-code-display">Your Code: <strong>{affiliateCode}</strong></p>
          </div>
          
          <div className="affiliate-stats-grid">
            <div className="affiliate-stat">
              <span className="stat-value">0</span>
              <span className="stat-label">Total Referrals</span>
            </div>
            <div className="affiliate-stat">
              <span className="stat-value">$0</span>
              <span className="stat-label">Pending Earnings</span>
            </div>
            <div className="affiliate-stat">
              <span className="stat-value">$0</span>
              <span className="stat-label">Total Paid</span>
            </div>
          </div>
        </div>
      )}

      {/* Commission Structure */}
      <div className="affiliate-commission-card">
        <h3>Commission Structure</h3>
        <div className="commission-table-container">
          <table className="affiliate-commission-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Price</th>
                <th>Your Commission (10%)</th>
                <th>Recurring?</th>
              </tr>
            </thead>
            <tbody>
              {commissionData.map((item, idx) => (
                <tr key={idx} className={item.recurring ? 'highlight' : ''}>
                  <td>{item.service}</td>
                  <td>{item.price}</td>
                  <td className="commission-value">{item.commission}</td>
                  <td>{item.recurring ? '✓ Recurring' : 'One-time'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="commission-note">
          <strong>Note:</strong> Recurring commissions continue as long as your referral remains a member.
        </p>
      </div>
    </div>
  );
};

// Messages View Component
const MessagesView = ({ messages, token }) => {
  return (
    <div className="messages-view">
      {messages.length > 0 ? (
        <div className="messages-list">
          {messages.map(msg => (
            <div key={msg.id} className={`message-card ${!msg.is_read ? 'unread' : ''}`}>
              <div className="message-header">
                <span className="sender">{msg.sender_name}</span>
                <span className="date">
                  {new Date(msg.created_at).toLocaleString()}
                </span>
              </div>
              <h4 className="message-subject">{msg.subject}</h4>
              <p className="message-preview">{msg.content.substring(0, 150)}...</p>
              {!msg.is_read && <span className="unread-badge">New</span>}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <MessageSquare size={64} />
          <h3>No Messages</h3>
          <p>Your secure messages will appear here.</p>
        </div>
      )}
    </div>
  );
};

// Documents View Component
const DocumentsView = ({ token, cases }) => {
  const [documents, setDocuments] = useState([]);
  const [categorySummary, setCategorySummary] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('other');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [storageConfigured, setStorageConfigured] = useState(false);
  const fileInputRef = useRef(null);

  const categoryMap = {
    'Medical Records': 'medical_records',
    'Lab Results': 'lab_results',
    'Imaging': 'imaging',
    'Pathology': 'pathology',
    'Treatment Plans': 'treatment_plans',
    'Medications': 'medications',
    'Specialist Notes': 'specialist_notes',
    'Insurance': 'insurance',
    'Other': 'other'
  };

  const categoryDisplay = {
    'medical_records': 'Medical Records',
    'lab_results': 'Lab Results',
    'imaging': 'Imaging',
    'pathology': 'Pathology',
    'treatment_plans': 'Treatment Plans',
    'medications': 'Medications',
    'specialist_notes': 'Specialist Notes',
    'insurance': 'Insurance',
    'other': 'Other'
  };

  useEffect(() => {
    checkStorageStatus();
    fetchDocuments();
    fetchCategorySummary();
  }, []);

  const checkStorageStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/storage/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStorageConfigured(data.configured);
    } catch (err) {
      console.error('Storage status check failed:', err);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Fetch documents failed:', err);
    }
  };

  const fetchCategorySummary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/documents/categories/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCategorySummary(data.categories || {});
    } catch (err) {
      console.error('Fetch summary failed:', err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const uploadFile = async (file) => {
    setError('');
    setSuccess('');
    setUploading(true);
    setUploadProgress(0);

    // Validate file size
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large. Maximum size is 50MB.');
      setUploading(false);
      return;
    }

    // Validate file type
    const allowedTypes = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'dicom', 'dcm'];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      setError(`File type .${ext} not allowed. Allowed: ${allowedTypes.join(', ')}`);
      setUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', selectedCategory);
    formData.append('description', description);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(`${API_URL}/api/portal/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const data = await response.json();
        setSuccess(`${file.name} uploaded successfully!`);
        setDescription('');
        fetchDocuments();
        fetchCategorySummary();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Upload failed. Please try again.');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Upload failed. Please check your connection and try again.');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const downloadDocument = async (docId, filename) => {
    try {
      const response = await fetch(`${API_URL}/api/portal/documents/${docId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download document.');
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/portal/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSuccess('Document deleted.');
        fetchDocuments();
        fetchCategorySummary();
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete document.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!storageConfigured) {
    return (
      <div className="documents-view" data-testid="documents-view">
        <div className="storage-not-configured">
          <AlertCircle size={48} />
          <h3>Document Storage Setup in Progress</h3>
          <p>Secure document storage is being configured. Please check back soon or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-view" data-testid="documents-view">
      {/* Upload Section */}
      <div className="upload-section">
        <div 
          className={`upload-zone ${uploading ? 'uploading' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.dicom,.dcm"
            style={{ display: 'none' }}
          />
          
          {uploading ? (
            <>
              <div className="upload-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <span>{uploadProgress}% Uploading...</span>
              </div>
            </>
          ) : (
            <>
              <Upload size={48} />
              <h3>Upload Medical Documents</h3>
              <p>Drag and drop files here or click to browse</p>
              <p className="file-types">Supported: PDF, JPG, PNG, DOC, DOCX, DICOM (Max 50MB)</p>
            </>
          )}
        </div>

        {/* Category & Description Selection */}
        <div className="upload-options">
          <div className="option-group">
            <label>Document Category:</label>
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              disabled={uploading}
            >
              {Object.entries(categoryMap).map(([display, value]) => (
                <option key={value} value={value}>{display}</option>
              ))}
            </select>
          </div>
          <div className="option-group">
            <label>Description (optional):</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Blood work from Jan 2026"
              disabled={uploading}
            />
          </div>
        </div>

        {/* Status Messages */}
        {error && <div className="upload-error"><AlertCircle size={16} /> {error}</div>}
        {success && <div className="upload-success"><CheckCircle size={16} /> {success}</div>}
      </div>
      
      {/* Category Summary */}
      <div className="document-categories">
        <h3>Document Categories</h3>
        <div className="categories-grid">
          {Object.entries(categoryMap).map(([display, value]) => (
            <div 
              key={value} 
              className={`category-card ${selectedCategory === value ? 'selected' : ''}`}
              onClick={() => setSelectedCategory(value)}
            >
              <FileText size={24} />
              <span>{display}</span>
              <span className="count">{categorySummary[value]?.count || 0} files</span>
            </div>
          ))}
        </div>
      </div>

      {/* Documents List */}
      {documents.length > 0 && (
        <div className="documents-list">
          <h3>Your Documents ({documents.length})</h3>
          <div className="documents-table">
            {documents.map(doc => (
              <div key={doc.id} className="document-row">
                <div className="doc-icon">
                  <FileText size={20} />
                </div>
                <div className="doc-info">
                  <span className="doc-name">{doc.filename}</span>
                  <span className="doc-meta">
                    {categoryDisplay[doc.category] || doc.category} • {formatFileSize(doc.size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                  </span>
                  {doc.description && <span className="doc-desc">{doc.description}</span>}
                </div>
                <div className="doc-actions">
                  <button 
                    className="btn-icon" 
                    onClick={() => downloadDocument(doc.id, doc.filename)}
                    title="Download"
                  >
                    <Download size={18} />
                  </button>
                  <button 
                    className="btn-icon danger" 
                    onClick={() => deleteDocument(doc.id)}
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// Purchase Services View Component
const PurchaseView = ({ onPurchase }) => {
  // Tracks which packages have the Medical Script Writing add-on toggled on.
  const [scriptAddOn, setScriptAddOn] = useState({});
  const SCRIPT_ADDON_PRICE = 175;

  const services = [
    {
      id: 'diagnostic_review',
      name: 'Diagnostic Bundle Review',
      subtitle: 'Case Clarification',
      price: 495,
      description: 'Complete review of your diagnostic materials with written summary.',
      features: [
        'Complete diagnostic material review',
        'Lab result analysis',
        'Written summary report',
        'Clarification of findings',
        'Up to 2 follow-up questions via email'
      ],
      type: 'diagnostic-review',
      // This tier does NOT include a doctor script — offer it à la carte.
      scriptEligible: true
    },
    {
      id: 'advocacy_launch',
      name: 'Advocacy Launch Session',
      subtitle: '30-Minute Intensive',
      price: 1197,
      description: 'A focused 30-minute session translating your complex history into actionable strategy.',
      features: [
        'Personalized 30-minute video consultation',
        'Customized doctor conversation script',
        'Prioritized testing roadmap',
        'Advocacy training document',
        'Post-consultation action plan'
      ],
      featured: true,
      type: 'advocacy-launch'
    },
    {
      id: 'deep_dive',
      name: 'Deep Dive Consultation',
      subtitle: '2-Hour Comprehensive',
      price: 1797,
      description: '2-hour comprehensive consultation with full advocacy strategy.',
      features: [
        'Extended 2-hour consultation',
        'Complete medical history review',
        'Detailed medical script creation',
        'Treatment options discussion',
        'One follow-up email within 14 days'
      ],
      type: 'deep_dive'
    }
  ];

  return (
    <div className="purchase-view" data-testid="purchase-view">
      <div className="purchase-header">
        <img src="/cadence-emblem-velvet.png" alt="CADENCE" className="purchase-crest" />
        <h2>Choose Your Service Package</h2>
        <p>Select the level of support that best fits your healthcare needs.</p>
      </div>

      <div className="purchase-grid">
        {services.map((service) => {
          const hasScript = !!scriptAddOn[service.id];
          const addOns = hasScript ? ['medical_script'] : [];
          const total = service.price + (hasScript ? SCRIPT_ADDON_PRICE : 0);
          return (
          <div key={service.id} className={`purchase-card ${service.featured ? 'featured' : ''}`}>
            {service.featured && (
              <div className="featured-badge">Most Popular</div>
            )}
            <div className="purchase-card-header">
              <h3>{service.name}</h3>
              <p className="service-subtitle">{service.subtitle}</p>
              <div className="price-display">
                <span className="price">${total}</span>
                <span className="price-type">one-time</span>
              </div>
            </div>
            <p className="service-description">{service.description}</p>
            <ul className="feature-list">
              {service.features.map((feature, idx) => (
                <li key={idx}>
                  <CheckCircle size={16} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            {service.scriptEligible && (
              <button
                type="button"
                className={`script-addon-toggle ${hasScript ? 'added' : ''}`}
                onClick={() => setScriptAddOn((prev) => ({ ...prev, [service.id]: !prev[service.id] }))}
                data-testid={`script-addon-${service.id}`}
                aria-pressed={hasScript}
              >
                <span className="script-addon-check">
                  {hasScript ? <CheckCircle size={18} /> : <Plus size={18} />}
                </span>
                <span className="script-addon-text">
                  <span className="script-addon-title">
                    {hasScript ? 'Medical Script Writing added' : 'Add Medical Script Writing'}
                  </span>
                  <span className="script-addon-sub">Custom conversation script for your upcoming appointment</span>
                </span>
                <span className="script-addon-price">+${SCRIPT_ADDON_PRICE}</span>
              </button>
            )}
            <button 
              className={service.featured ? 'btn-primary' : 'btn-outline'}
              onClick={() => onPurchase(service.type, total, addOns)}
              data-testid={`purchase-${service.id}`}
            >
              {hasScript ? `Purchase Now — $${total}` : 'Purchase Now'}
            </button>
            <p className="payment-note">Square secure payments</p>
          </div>
          );
        })}
      </div>

      {/* Membership Upsell */}
      <div className="membership-upsell">
        <h3>Need Ongoing Support?</h3>
        <p>Our membership plans provide continuous partnership for managing complex health journeys.</p>
        <div className="membership-preview">
          <div className="membership-tier">
            <span className="tier-name">Navigator</span>
            <span className="tier-price">$179/mo</span>
          </div>
          <div className="membership-tier">
            <span className="tier-name">Advocate</span>
            <span className="tier-price">$399/mo</span>
          </div>
          <div className="membership-tier">
            <span className="tier-name">Concierge</span>
            <span className="tier-price">$799/mo</span>
          </div>
        </div>
        <Link to="/membership" className="btn-gold">View Membership Plans</Link>
      </div>
    </div>
  );
};

// AI Analysis Results Component
const AIAnalysisResults = ({ analysis, onContinue, user }) => {
  // This component is now internal - only shown to CADENCE reviewers
  // Keeping it for admin dashboard use
  const [expandedPattern, setExpandedPattern] = useState(null);

  const getCategoryIcon = (category) => {
    const icons = {
      thyroid: Zap,
      autoimmune: Shield,
      hormonal: Heart,
      metabolic: TrendingUp,
      gut: Activity,
      adrenal: Brain,
      sleep: Clock,
      other: AlertCircle
    };
    return icons[category] || AlertCircle;
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high': return '#D4AF37';
      case 'medium': return '#F59E0B';
      case 'low': return '#6B7280';
      default: return '#6B7280';
    }
  };

  if (!analysis) {
    return null;
  }

  return (
    <div className="ai-analysis-results internal-only" data-testid="ai-analysis-internal">
      {/* This is for internal review only */}
    </div>
  );
};

// Thank You Letter Component - Shown to clients after submission
const ThankYouLetter = ({ user, selectedPackage, onContinue }) => {
  const packageSteps = {
    'diagnostic-review': {
      name: 'Diagnostic Bundle Review',
      timeline: '3-5 business days',
      steps: [
        'Our medical team will thoroughly review your intake form and all submitted records',
        'Dr. Stewart will analyze your diagnostic materials and lab results',
        'You will receive a comprehensive written summary with clarifications',
        'We will reach out if we need any additional information'
      ],
      nextAction: 'Please ensure you have sent all relevant medical records to CADENCE@cadencemedicalconsulting.com'
    },
    'advocacy-launch': {
      name: 'Advocacy Launch Session',
      timeline: '24-48 hours',
      steps: [
        'Our team will carefully review your intake to prepare for your session',
        'You will receive an email to schedule your 30-minute video consultation',
        'Before your session, we will prepare your customized doctor conversation script',
        'During your session, we will walk through your personalized testing roadmap and action plan'
      ],
      nextAction: 'Check your email for scheduling instructions within 24-48 hours'
    },
    'deep-dive': {
      name: 'Deep Dive Consultation',
      timeline: '24-48 hours',
      steps: [
        'Our medical team will conduct a comprehensive review of your intake and medical history',
        'You will receive priority scheduling for your 2-hour consultation',
        'Dr. Stewart will personally prepare your detailed medical script and advocacy strategy',
        'Your session will include ongoing support planning and follow-up coordination'
      ],
      nextAction: 'Check your email for priority scheduling within 24-48 hours'
    }
  };

  const pkg = packageSteps[selectedPackage] || packageSteps['diagnostic-review'];

  return (
    <div className="thank-you-letter" data-testid="thank-you-letter">
      <div className="letter-container">
        {/* Header */}
        <div className="letter-header">
          <img src="/cadence-emblem-velvet.png" alt="CADENCE" className="letter-crest" />
          <div className="header-content">
            <h1>Thank You for Trusting CADENCE</h1>
            <p className="subtitle">Your health journey matters to us</p>
          </div>
        </div>

        {/* Personal Greeting */}
        <div className="letter-body">
          <div className="greeting">
            <p>Dear {user?.first_name || 'Valued Patient'},</p>
          </div>

          <div className="letter-content">
            <p>
              Thank you for choosing CADENCE to be your advocate. 
              We understand that navigating the healthcare system can feel overwhelming, 
              and we are honored that you've trusted us with your health story.
            </p>

            <p>
              <strong>We see you. We hear you. And we are committed to helping you get the answers you deserve.</strong>
            </p>

            <p>
              Your comprehensive intake has been securely received. Our team will review 
              every detail with the care and attention your health deserves.
            </p>
          </div>

          {/* Package Details */}
          <div className="package-confirmation">
            <div className="package-header">
              <CheckCircle size={24} />
              <h3>Your {pkg.name}</h3>
            </div>
            
            <div className="what-happens-next">
              <h4>What Happens Next:</h4>
              <ol className="steps-list">
                {pkg.steps.map((step, idx) => (
                  <li key={idx}>
                    <span className="step-number">{idx + 1}</span>
                    <span className="step-text">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="timeline-box">
              <Clock size={20} />
              <span>Expected Response Time: <strong>{pkg.timeline}</strong></span>
            </div>

            <div className="next-action-box">
              <AlertCircle size={20} />
              <span>{pkg.nextAction}</span>
            </div>
          </div>

          {/* Closing */}
          <div className="letter-closing">
            <p>
              If you have any questions or need to provide additional information, 
              please don't hesitate to reach out to us at{' '}
              <a href="mailto:CADENCE@cadencemedicalconsulting.com">CADENCE@cadencemedicalconsulting.com</a>.
            </p>
            
            <p>
              With warmth and dedication to your health,
            </p>

            <div className="signature-block">
              <p className="signature-name">The CADENCE Team</p>
              <p className="signature-title">Andera N. Peeples, CEO & Dr. Lakia S. Stewart, DNP, FNP-C, WHNP-BC</p>
              <p className="signature-tagline">Clinical Advocacy & Decisive Empowerment</p>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="letter-actions">
          <button className="btn-primary" onClick={onContinue} data-testid="continue-to-dashboard">
            Continue to Your Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

// Comprehensive Health Intake Form Component
const ComprehensiveIntakeView = ({ token, user, paymentSuccess, onComplete, isGuest = false, isAuthenticated = false }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState({
    // Section 1: Core Narrative
    primary_health_concern: '',
    ideal_outcome: '',
    // Q3: Symptom checkboxes (multi-select)
    symptoms_fatigue: [],
    symptoms_pain: [],
    symptoms_digestive: [],
    symptoms_hormonal: [],
    symptoms_neurological: [],
    symptoms_cardiovascular: [],
    symptoms_respiratory: [],
    symptoms_skin: [],
    symptoms_other_text: '', // Free text for unlisted symptoms
    
    // Section 2: Medical & Surgical History
    current_diagnoses: '',
    past_diagnoses: '',
    surgical_history: '',
    hospitalizations: '',
    
    // Section 3: Medications & Supplements
    current_prescriptions: [{ medication: '', dosage: '', duration: '', side_effects: '' }],
    current_supplements: [{ supplement: '', dosage: '', reason: '' }],
    past_medications: '',
    
    // Section 4: Family History
    family_history: [
      { relation: 'Mother', age: '', conditions: '' },
      { relation: 'Father', age: '', conditions: '' },
      { relation: 'Sibling', age: '', conditions: '' }
    ],
    autoimmune_conditions: '',
    mental_health_family: '',
    addiction_history: '',
    
    // Section 5: Lifestyle & Environment
    diet_description: '',
    sleep_hours: '',
    sleep_quality: '',
    stress_level: '',
    stress_management: '',
    exercise_routine: '',
    environmental_exposures: '',
    
    // Section 6: Women's Health (if applicable)
    menstrual_cycle: '',
    pregnancies: '',
    menopause_symptoms: '',
    
    // Section 6: Review of Systems
    constitutional: [],
    heent: [],
    endocrine: [],
    gi: [],
    neuropsych: [],
    other_symptoms: [],
    
    // Section 7: Psychological & Behavioral Insights
    suspected_cause: '',
    health_fears: '',
    diagnosis_concerns: '',
    family_health_experiences: '',
    information_sources: '',
    trust_barriers: '',
    adherence_challenges: '',
    understanding_level: '',
    emotional_impact: '',
    
    // Section 8: Authorization
    consent_given: false,
    refund_policy_acknowledged: false,
    signature: '',
    signature_date: '',
    selected_package: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [selectedPackageDetails, setSelectedPackageDetails] = useState(null);

  // ============================================================
  // OFFLINE / DRAFT-PERSISTENCE WIRING
  // ------------------------------------------------------------
  // Every keystroke is mirrored to localStorage so a refresh,
  // dead battery, or lost signal can never wipe the patient's work.
  // Drafts auto-expire after 48 hours. If the patient hits Submit
  // while offline, the payload is queued and retried automatically
  // when the network returns.
  // ============================================================
  const [savedAt, setSavedAt] = useState(null);
  const [hasResumedDraft, setHasResumedDraft] = useState(false);
  const [pendingSubmitInfo, setPendingSubmitInfo] = useState(loadPendingSubmit());
  const draftHydrationRef = React.useRef(false);

  // 1) Hydrate any existing draft on mount (once).
  useEffect(() => {
    if (draftHydrationRef.current) return;
    draftHydrationRef.current = true;
    const draft = loadIntakeDraft();
    if (draft && draft.formData) {
      // Only rehydrate if the draft has meaningful content (not the
      // pristine defaults). We treat any non-empty primary concern OR
      // a non-zero section as "real" progress worth restoring.
      const hasContent =
        (draft.formData.primary_health_concern || '').trim().length > 0 ||
        (draft.currentSection || 0) > 0;
      if (hasContent) {
        setFormData((prev) => ({ ...prev, ...draft.formData }));
        if (typeof draft.currentSection === 'number') {
          setCurrentSection(draft.currentSection);
        }
        setSavedAt(draft.savedAt);
        setHasResumedDraft(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Auto-save (debounced) whenever the form changes.
  useEffect(() => {
    if (!draftHydrationRef.current) return; // wait for mount-hydration first
    const handle = setTimeout(() => {
      saveIntakeDraft(formData, currentSection);
      setSavedAt(Date.now());
    }, 600);
    return () => clearTimeout(handle);
  }, [formData, currentSection]);
  // ============================================================

  const packageOptions = [
    {
      id: 'diagnostic-review',
      name: 'Diagnostic Bundle Review',
      price: 495,
      description: 'Complete diagnostic material review with written summary',
      features: ['Lab result analysis', 'Written summary report', 'Clarification of findings']
    },
    {
      id: 'advocacy-launch',
      name: 'Advocacy Launch Session',
      price: 1197,
      popular: true,
      description: '30-minute intensive with personalized doctor script',
      features: ['Video consultation', 'Customized doctor script', 'Testing roadmap', 'Action plan']
    },
    {
      id: 'deep-dive',
      name: 'Deep Dive Consultation',
      price: 1797,
      description: '2-hour comprehensive consultation with full advocacy strategy',
      features: ['Extended consultation', 'Full medical review', 'Ongoing advocacy', 'Follow-up support']
    }
  ];

  const sections = [
    { id: 0, title: 'Primary Concerns', icon: Heart },
    { id: 1, title: 'Medical History', icon: Stethoscope },
    { id: 2, title: 'Medications', icon: FileText },
    { id: 3, title: 'Family History', icon: User },
    { id: 4, title: 'Lifestyle', icon: Activity },
    { id: 5, title: 'Review of Systems', icon: ClipboardList },
    { id: 6, title: 'Your Perspective', icon: Brain },
    { id: 7, title: 'Authorization', icon: Shield }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, subfield, value) => {
    setFormData(prev => {
      const arr = [...prev[field]];
      arr[index] = { ...arr[index], [subfield]: value };
      return { ...prev, [field]: arr };
    });
  };

  const addArrayItem = (field, template) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], template]
    }));
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const arr = prev[field];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...arr, value] };
      }
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Offline-aware: if the device has no network, we can't reach the API.
    // Queue the submission locally and let the patient know we'll send it
    // automatically when they reconnect. Their draft stays put either way.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      queuePendingSubmit(formData);
      setPendingSubmitInfo({ payload: formData, queuedAt: Date.now() });
      setIsSubmitting(false);
      alert(
        "You're offline right now. Your work is saved on this device and " +
        "will submit automatically as soon as you're back online."
      );
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/portal/intake/comprehensive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Grant full site access to survey completers
        grantSiteAccess();
        // Local draft + any queued submit are no longer needed once the
        // server has accepted the intake.
        clearIntakeDraft();
        clearPendingSubmit();
        setPendingSubmitInfo(null);
        
        // If Square checkout URL returned, redirect to payment
        if (data.square_checkout_url) {
          // Store intake_id for post-payment confirmation
          sessionStorage.setItem('pending_intake_id', data.intake_id);
          window.location.href = data.square_checkout_url;
        } else if (data.stripe_session_url) {
          // Fallback for Stripe (backwards compatibility)
          sessionStorage.setItem('pending_intake_id', data.intake_id);
          window.location.href = data.stripe_session_url;
        } else {
          // Fallback for already-paid or free intakes
          setSubmitSuccess(true);
          setSelectedPackageDetails(formData.selected_package);
        }
      } else {
        const errorData = await response.json();
        console.error('Submit error:', errorData);
        alert(errorData.detail || 'Failed to submit intake form. Please try again.');
      }
    } catch (err) {
      console.error('Submit error:', err);
      // Network failure during a submit attempt — same treatment as being
      // offline up front. Queue + reassure.
      queuePendingSubmit(formData);
      setPendingSubmitInfo({ payload: formData, queuedAt: Date.now() });
      alert(
        "We couldn't reach the server. Your answers are saved on this device " +
        "and will submit automatically as soon as the connection returns."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // 3) Auto-retry any queued submission as soon as we come back online.
  useEffect(() => {
    const tryFlushPending = async () => {
      const pending = loadPendingSubmit();
      if (!pending || !pending.payload) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      try {
        const response = await fetch(`${API_URL}/api/portal/intake/comprehensive`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(pending.payload)
        });
        if (response.ok) {
          const data = await response.json();
          grantSiteAccess();
          clearIntakeDraft();
          clearPendingSubmit();
          setPendingSubmitInfo(null);
          if (data.square_checkout_url) {
            sessionStorage.setItem('pending_intake_id', data.intake_id);
            window.location.href = data.square_checkout_url;
          } else if (data.stripe_session_url) {
            sessionStorage.setItem('pending_intake_id', data.intake_id);
            window.location.href = data.stripe_session_url;
          } else {
            setSubmitSuccess(true);
            setSelectedPackageDetails(pending.payload.selected_package);
          }
        }
      } catch {
        // Still no network — keep the queue around for the next attempt.
      }
    };
    // Attempt once on mount (covers re-opening the app while online), and
    // also on every "online" event.
    tryFlushPending();
    window.addEventListener('online', tryFlushPending);
    return () => window.removeEventListener('online', tryFlushPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Skip-around-friendly progress: counts sections that actually have meaningful content,
  // not just which tab the user is currently on. Gives the patient an honest read on what's
  // done regardless of the order they tackled it in.
  const isSectionFilled = useCallback((sectionIdx) => {
    const f = formData;
    const hasText = (s) => typeof s === 'string' && s.trim().length > 0;
    const hasArr = (a) => Array.isArray(a) && a.length > 0;
    const hasFilledRow = (rows, keys) =>
      Array.isArray(rows) && rows.some((r) => keys.some((k) => hasText(r?.[k])));
    switch (sectionIdx) {
      case 0:
        return hasText(f.primary_health_concern) || hasText(f.ideal_outcome) ||
          hasArr(f.symptoms_fatigue) || hasArr(f.symptoms_pain) || hasArr(f.symptoms_digestive) ||
          hasArr(f.symptoms_hormonal) || hasArr(f.symptoms_neurological) || hasArr(f.symptoms_cardiovascular) ||
          hasArr(f.symptoms_respiratory) || hasArr(f.symptoms_skin) || hasText(f.symptoms_other_text);
      case 1:
        return hasText(f.current_diagnoses) || hasText(f.past_diagnoses) ||
          hasText(f.surgical_history) || hasText(f.hospitalizations);
      case 2:
        return hasFilledRow(f.current_prescriptions, ['medication', 'dosage']) ||
          hasFilledRow(f.current_supplements, ['supplement', 'dosage']) ||
          hasText(f.past_medications);
      case 3:
        return hasFilledRow(f.family_history, ['conditions', 'age']) ||
          hasText(f.autoimmune_conditions) || hasText(f.mental_health_family) ||
          hasText(f.addiction_history);
      case 4:
        return hasText(f.diet_description) || hasText(f.sleep_hours) || hasText(f.sleep_quality) ||
          hasText(f.stress_level) || hasText(f.stress_management) || hasText(f.exercise_routine) ||
          hasText(f.environmental_exposures);
      case 5:
        return hasArr(f.constitutional) || hasArr(f.heent) || hasArr(f.endocrine) ||
          hasArr(f.gi) || hasArr(f.neuropsych) || hasArr(f.other_symptoms);
      case 6:
        return hasText(f.suspected_cause) || hasText(f.health_fears) || hasText(f.diagnosis_concerns) ||
          hasText(f.family_health_experiences) || hasText(f.information_sources) ||
          hasText(f.trust_barriers) || hasText(f.adherence_challenges) ||
          hasText(f.understanding_level) || hasText(f.emotional_impact);
      case 7:
        return Boolean(f.consent_given) && Boolean(f.refund_policy_acknowledged) &&
          hasText(f.signature) && hasText(f.selected_package);
      default:
        return false;
    }
  }, [formData]);

  const filledSections = sections.reduce((acc, _s, idx) => acc + (isSectionFilled(idx) ? 1 : 0), 0);
  const progress = Math.round((filledSections / sections.length) * 100);

  // One-time hint banner letting users know they can tackle sections in any order.
  const [skipHintDismissed, setSkipHintDismissed] = useState(() => {
    try {
      return localStorage.getItem('cadence_intake_skip_hint_dismissed') === '1';
    } catch {
      return false;
    }
  });
  const dismissSkipHint = () => {
    try { localStorage.setItem('cadence_intake_skip_hint_dismissed', '1'); } catch { /* ignore */ }
    setSkipHintDismissed(true);
  };

  // Show thank you letter if payment was successful (returned from Square/Stripe)
  // or if form was submitted successfully
  if (submitSuccess || paymentSuccess) {
    return (
      <ThankYouLetter 
        user={user}
        selectedPackage={selectedPackageDetails || formData.selected_package}
        onContinue={onComplete}
      />
    );
  }

  return (
    <div className="comprehensive-intake" data-testid="comprehensive-intake">
      {/* Soft mid-survey nudge for guest visitors only — dismissible, never gates */}
      <GuestSaveSpotNudge
        progressPercent={progress}
        isGuest={isGuest}
        isAuthenticated={isAuthenticated}
      />

      {/* Quiet floating bookmark — always available for guests, hover/tap to expand */}
      <GuestSaveSpotBreadcrumb
        isGuest={isGuest}
        isAuthenticated={isAuthenticated}
        progressPercent={progress}
      />

      {/* Header */}
      <div className="intake-form-header">
        <img src="/cadence-emblem-velvet.png" alt="CADENCE" className="intake-crest" />
        <div className="intake-title">
          <h2>CADENCE Medical Consulting, LLC</h2>
          <h3>Comprehensive Health Intake Form</h3>
        </div>
      </div>

      <div className="intake-note">
        <p><strong>A Note to You:</strong> This form is designed to help us see the full picture of your health. The more detail you provide, the more accurately we can connect the dots and provide you with meaningful, personalized insights. Please take your time.</p>
      </div>

      {/* Resume-draft banner — only shown when we rehydrated an in-flight
          draft from localStorage on this visit. Lets the patient know
          their previous work was kept safe; dismissible. */}
      {hasResumedDraft && (
        <div className="intake-resume-banner" data-testid="intake-resume-banner">
          <div className="intake-resume-banner-icon" aria-hidden="true">
            <ClipboardList size={18} />
          </div>
          <p>
            <strong>Welcome back.</strong> We picked up exactly where you
            left off &mdash; your answers were saved on this device.
          </p>
          <button
            type="button"
            className="intake-resume-banner-close"
            onClick={() => setHasResumedDraft(false)}
            aria-label="Dismiss"
            data-testid="intake-resume-banner-close"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Progress */}
      <div className="intake-progress">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
        <span className="progress-text">{progress}% Complete</span>
        {/* Offline / auto-save status — lives inline with the progress so
            the patient can always see their work is captured. */}
        <OfflineIntakeBadge
          savedAt={savedAt}
          hasPendingSubmit={!!pendingSubmitInfo}
        />
      </div>

      {/* One-time hint: clients can tackle sections in any order */}
      {!skipHintDismissed && (
        <div className="intake-skip-hint" data-testid="intake-skip-hint">
          <div className="intake-skip-hint-icon">
            <Shuffle size={16} />
          </div>
          <p>
            <strong>Take it in any order.</strong> Tap any section below to jump
            in &mdash; we&rsquo;ll save your progress as you go and mark each
            section complete once you&rsquo;ve added something there.
          </p>
          <button
            type="button"
            className="intake-skip-hint-close"
            onClick={dismissSkipHint}
            aria-label="Dismiss tip"
            data-testid="intake-skip-hint-close"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Section Navigation */}
      <div className="section-nav">
        {sections.map((section, idx) => {
          const filled = isSectionFilled(idx);
          return (
            <button
              key={section.id}
              className={`section-nav-item ${idx === currentSection ? 'active' : ''} ${filled ? 'completed' : ''}`}
              onClick={() => setCurrentSection(idx)}
              data-testid={`section-nav-item-${idx}`}
              title={filled ? 'Has content — tap to revisit' : 'Tap to start this section'}
            >
              <section.icon size={18} />
              <span>{section.title}</span>
              {filled && (
                <span className="section-nav-checkmark" aria-label="Has content">✓</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Form Sections */}
      <div className="intake-form-body">
        {/* Section 1: Core Narrative */}
        {currentSection === 0 && (
          <div className="form-section" data-testid="section-primary-concerns">
            <h3>Section 1: The Core Narrative</h3>
            
            <div className="form-group">
              <label>1. What is the single most important health concern you want to resolve?</label>
              <p className="form-hint">Please describe in your own words</p>
              <textarea
                value={formData.primary_health_concern}
                onChange={(e) => handleInputChange('primary_health_concern', e.target.value)}
                placeholder="Describe your primary health concern..."
                rows="4"
                data-testid="primary-concern-input"
              />
            </div>

            <div className="form-group">
              <label>2. What does your ideal health outcome look like in one year?</label>
              <p className="form-hint">e.g., Have energy to play with my kids, Understand what&apos;s causing my brain fog, Get off my blood pressure medication</p>
              <textarea
                value={formData.ideal_outcome}
                onChange={(e) => handleInputChange('ideal_outcome', e.target.value)}
                placeholder="Describe your ideal outcome..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>3. Please check all symptoms you are currently experiencing:</label>
              <p className="form-hint">Select all that apply in each category</p>
              
              <div className="checkbox-grid symptoms-grid">
                <div className="checkbox-group">
                  <h4>Fatigue & Energy</h4>
                  {['Chronic fatigue', 'Morning exhaustion', 'Afternoon energy crash', 'Exercise intolerance', 'Unrefreshing sleep'].map(item => (
                    <label key={item} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.symptoms_fatigue.includes(item)}
                        onChange={() => handleCheckboxChange('symptoms_fatigue', item)}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>

                <div className="checkbox-group">
                  <h4>Pain & Discomfort</h4>
                  {['Headaches/Migraines', 'Joint pain', 'Muscle aches', 'Back pain', 'Neck/shoulder tension', 'Chest discomfort'].map(item => (
                    <label key={item} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.symptoms_pain.includes(item)}
                        onChange={() => handleCheckboxChange('symptoms_pain', item)}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>

                <div className="checkbox-group">
                  <h4>Digestive</h4>
                  {['Bloating', 'Gas', 'Heartburn/Reflux', 'Constipation', 'Diarrhea', 'Nausea', 'Food sensitivities'].map(item => (
                    <label key={item} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.symptoms_digestive.includes(item)}
                        onChange={() => handleCheckboxChange('symptoms_digestive', item)}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>

                <div className="checkbox-group">
                  <h4>Hormonal</h4>
                  {['Hot flashes', 'Night sweats', 'Weight changes', 'Hair loss/thinning', 'Irregular periods', 'Low libido', 'Temperature sensitivity'].map(item => (
                    <label key={item} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.symptoms_hormonal.includes(item)}
                        onChange={() => handleCheckboxChange('symptoms_hormonal', item)}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>

                <div className="checkbox-group">
                  <h4>Neurological & Mental</h4>
                  {['Brain fog', 'Memory issues', 'Difficulty concentrating', 'Anxiety', 'Depression', 'Mood swings', 'Dizziness', 'Numbness/tingling'].map(item => (
                    <label key={item} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.symptoms_neurological.includes(item)}
                        onChange={() => handleCheckboxChange('symptoms_neurological', item)}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>

                <div className="checkbox-group">
                  <h4>Cardiovascular</h4>
                  {['Heart palpitations', 'Shortness of breath', 'Swelling (legs/ankles)', 'High/low blood pressure', 'Poor circulation'].map(item => (
                    <label key={item} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.symptoms_cardiovascular.includes(item)}
                        onChange={() => handleCheckboxChange('symptoms_cardiovascular', item)}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>

                <div className="checkbox-group">
                  <h4>Skin & Appearance</h4>
                  {['Rashes', 'Dry skin', 'Acne', 'Hives', 'Easy bruising', 'Slow wound healing'].map(item => (
                    <label key={item} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.symptoms_skin.includes(item)}
                        onChange={() => handleCheckboxChange('symptoms_skin', item)}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>

                <div className="checkbox-group">
                  <h4>Sleep & Rest</h4>
                  {['Insomnia', 'Waking frequently', 'Sleep apnea symptoms', 'Restless legs', 'Vivid dreams/nightmares'].map(item => (
                    <label key={item} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.symptoms_respiratory.includes(item)}
                        onChange={() => handleCheckboxChange('symptoms_respiratory', item)}
                      />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="form-group other-symptoms-group">
                <label>Other symptoms not listed above:</label>
                <textarea
                  value={formData.symptoms_other_text}
                  onChange={(e) => handleInputChange('symptoms_other_text', e.target.value)}
                  placeholder="Describe any other symptoms you experience..."
                  rows="3"
                />
              </div>
            </div>
          </div>
        )}

        {/* Section 2: Medical & Surgical History */}
        {currentSection === 1 && (
          <div className="form-section" data-testid="section-medical-history">
            <h3>Section 2: Medical & Surgical History (The Timeline)</h3>
            
            <div className="form-group">
              <label>4. Current Diagnoses</label>
              <p className="form-hint">List all current medical conditions (e.g., High Blood Pressure, PCOS, Migraines, IBS)</p>
              <textarea
                value={formData.current_diagnoses}
                onChange={(e) => handleInputChange('current_diagnoses', e.target.value)}
                placeholder="List your current diagnoses..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>5. Past Diagnoses</label>
              <p className="form-hint">List any significant past medical conditions, even if resolved</p>
              <textarea
                value={formData.past_diagnoses}
                onChange={(e) => handleInputChange('past_diagnoses', e.target.value)}
                placeholder="List past diagnoses..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>6. Surgical History</label>
              <p className="form-hint">List all surgeries and the years they were performed</p>
              <textarea
                value={formData.surgical_history}
                onChange={(e) => handleInputChange('surgical_history', e.target.value)}
                placeholder="e.g., Appendectomy (2015), C-section (2018)..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>7. Hospitalizations</label>
              <p className="form-hint">List reasons for any hospitalizations and approximate years</p>
              <textarea
                value={formData.hospitalizations}
                onChange={(e) => handleInputChange('hospitalizations', e.target.value)}
                placeholder="List hospitalizations..."
                rows="3"
              />
            </div>
          </div>
        )}

        {/* Section 3: Medications & Supplements */}
        {currentSection === 2 && (
          <div className="form-section" data-testid="section-medications">
            <h3>Section 3: Medication & Supplement History (The Chemical Influences)</h3>
            
            <div className="form-group">
              <label>8. Current Prescriptions</label>
              <div className="medication-table">
                <div className="table-header">
                  <span>Medication</span>
                  <span>Dosage</span>
                  <span>For How Long?</span>
                  <span>Any Side Effects?</span>
                </div>
                {formData.current_prescriptions.map((item, idx) => (
                  <div key={idx} className="table-row">
                    <input
                      type="text"
                      value={item.medication}
                      onChange={(e) => handleArrayChange('current_prescriptions', idx, 'medication', e.target.value)}
                      placeholder="e.g., Synthroid"
                    />
                    <input
                      type="text"
                      value={item.dosage}
                      onChange={(e) => handleArrayChange('current_prescriptions', idx, 'dosage', e.target.value)}
                      placeholder="50 mcg"
                    />
                    <input
                      type="text"
                      value={item.duration}
                      onChange={(e) => handleArrayChange('current_prescriptions', idx, 'duration', e.target.value)}
                      placeholder="5 years"
                    />
                    <input
                      type="text"
                      value={item.side_effects}
                      onChange={(e) => handleArrayChange('current_prescriptions', idx, 'side_effects', e.target.value)}
                      placeholder="None"
                    />
                  </div>
                ))}
                <button 
                  type="button" 
                  className="add-row-btn"
                  onClick={() => addArrayItem('current_prescriptions', { medication: '', dosage: '', duration: '', side_effects: '' })}
                >
                  + Add Another Medication
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>9. Current Supplements</label>
              <div className="supplement-table">
                <div className="table-header">
                  <span>Supplement</span>
                  <span>Dosage</span>
                  <span>Why are you taking it?</span>
                </div>
                {formData.current_supplements.map((item, idx) => (
                  <div key={idx} className="table-row">
                    <input
                      type="text"
                      value={item.supplement}
                      onChange={(e) => handleArrayChange('current_supplements', idx, 'supplement', e.target.value)}
                      placeholder="e.g., Vitamin D3"
                    />
                    <input
                      type="text"
                      value={item.dosage}
                      onChange={(e) => handleArrayChange('current_supplements', idx, 'dosage', e.target.value)}
                      placeholder="2000 IU"
                    />
                    <input
                      type="text"
                      value={item.reason}
                      onChange={(e) => handleArrayChange('current_supplements', idx, 'reason', e.target.value)}
                      placeholder="Doctor said I was low"
                    />
                  </div>
                ))}
                <button 
                  type="button" 
                  className="add-row-btn"
                  onClick={() => addArrayItem('current_supplements', { supplement: '', dosage: '', reason: '' })}
                >
                  + Add Another Supplement
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>10. Past Medications/Supplements</label>
              <p className="form-hint">List any significant past prescriptions or supplements you&apos;ve stopped and why</p>
              <textarea
                value={formData.past_medications}
                onChange={(e) => handleInputChange('past_medications', e.target.value)}
                placeholder="e.g., Was on Metformin for PCOS but it caused GI upset, Took Ashwagandha but it made me anxious"
                rows="3"
              />
            </div>
          </div>
        )}

        {/* Section 4: Family History */}
        {currentSection === 3 && (
          <div className="form-section" data-testid="section-family-history">
            <h3>Section 4: Family History (The Genetic & Environmental Blueprint)</h3>
            <p className="section-intro">Please list first-degree relatives (Parents, Siblings, Children) and their health conditions, or cause/age of death.</p>
            
            <div className="form-group">
              <label>Family Health History</label>
              <div className="family-table">
                <div className="table-header">
                  <span>Relation</span>
                  <span>Age (or Age at Death)</span>
                  <span>Health Conditions (Please be specific)</span>
                </div>
                {formData.family_history.map((item, idx) => (
                  <div key={idx} className="table-row">
                    <input
                      type="text"
                      value={item.relation}
                      onChange={(e) => handleArrayChange('family_history', idx, 'relation', e.target.value)}
                      placeholder="Mother"
                    />
                    <input
                      type="text"
                      value={item.age}
                      onChange={(e) => handleArrayChange('family_history', idx, 'age', e.target.value)}
                      placeholder="65"
                    />
                    <input
                      type="text"
                      value={item.conditions}
                      onChange={(e) => handleArrayChange('family_history', idx, 'conditions', e.target.value)}
                      placeholder="e.g., Hypothyroidism, Osteoporosis, Breast Cancer (dx 65)"
                    />
                  </div>
                ))}
                <button 
                  type="button" 
                  className="add-row-btn"
                  onClick={() => addArrayItem('family_history', { relation: '', age: '', conditions: '' })}
                >
                  + Add Another Family Member
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Key Patterns to Note:</label>
              <div className="pattern-group">
                <label className="sub-label">Autoimmune Conditions (e.g., Hashimoto&apos;s, Rheumatoid Arthritis, Lupus, MS)</label>
                <input
                  type="text"
                  value={formData.autoimmune_conditions}
                  onChange={(e) => handleInputChange('autoimmune_conditions', e.target.value)}
                  placeholder="List any autoimmune conditions in family..."
                />
              </div>
              <div className="pattern-group">
                <label className="sub-label">Mental Health (e.g., Depression, Anxiety, Bipolar, ADHD)</label>
                <input
                  type="text"
                  value={formData.mental_health_family}
                  onChange={(e) => handleInputChange('mental_health_family', e.target.value)}
                  placeholder="List any mental health conditions in family..."
                />
              </div>
              <div className="pattern-group">
                <label className="sub-label">Addiction (e.g., Alcoholism, Substance Use)</label>
                <input
                  type="text"
                  value={formData.addiction_history}
                  onChange={(e) => handleInputChange('addiction_history', e.target.value)}
                  placeholder="List any addiction history in family..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Section 5: Lifestyle & Environment */}
        {currentSection === 4 && (
          <div className="form-section" data-testid="section-lifestyle">
            <h3>Section 5: Lifestyle & Environment (The Modern Triggers)</h3>
            
            <div className="form-group">
              <label>11. Diet</label>
              <p className="form-hint">Briefly describe your typical daily diet. What do you eat for breakfast, lunch, dinner, snacks?</p>
              <textarea
                value={formData.diet_description}
                onChange={(e) => handleInputChange('diet_description', e.target.value)}
                placeholder="Describe your typical daily diet..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>12. Sleep</label>
              <p className="form-hint">On average, how many hours do you sleep? Do you wake up feeling rested? Do you have trouble falling or staying asleep?</p>
              <div className="inline-inputs">
                <div className="inline-group">
                  <label className="sub-label">Hours per night</label>
                  <input
                    type="text"
                    value={formData.sleep_hours}
                    onChange={(e) => handleInputChange('sleep_hours', e.target.value)}
                    placeholder="7"
                  />
                </div>
                <div className="inline-group flex-grow">
                  <label className="sub-label">Sleep Quality & Issues</label>
                  <input
                    type="text"
                    value={formData.sleep_quality}
                    onChange={(e) => handleInputChange('sleep_quality', e.target.value)}
                    placeholder="e.g., Trouble staying asleep, wake up tired"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>13. Stress & Mental Health</label>
              <p className="form-hint">How would you rate your daily stress level (1-10)? What are your primary stressors? How do you manage stress?</p>
              <div className="inline-inputs">
                <div className="inline-group">
                  <label className="sub-label">Stress Level (1-10)</label>
                  <input
                    type="text"
                    value={formData.stress_level}
                    onChange={(e) => handleInputChange('stress_level', e.target.value)}
                    placeholder="7"
                  />
                </div>
                <div className="inline-group flex-grow">
                  <label className="sub-label">Stressors & Management</label>
                  <input
                    type="text"
                    value={formData.stress_management}
                    onChange={(e) => handleInputChange('stress_management', e.target.value)}
                    placeholder="Work stress, manage with exercise"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>14. Exercise</label>
              <p className="form-hint">What type and frequency of exercise do you get?</p>
              <input
                type="text"
                value={formData.exercise_routine}
                onChange={(e) => handleInputChange('exercise_routine', e.target.value)}
                placeholder="e.g., Walk 30 min 3x/week, yoga 2x/week"
              />
            </div>

            <div className="form-group">
              <label>15. Environmental Exposures</label>
              <p className="form-hint">Have you ever lived or worked in an environment with mold, heavy chemicals, or significant air pollution?</p>
              <textarea
                value={formData.environmental_exposures}
                onChange={(e) => handleInputChange('environmental_exposures', e.target.value)}
                placeholder="Describe any environmental exposures..."
                rows="2"
              />
            </div>

            <div className="form-group womens-health">
              <label>16. For Women Only</label>
              <div className="inline-inputs">
                <div className="inline-group">
                  <label className="sub-label">Periods: Regular/Irregular? Painful? Heavy/Light?</label>
                  <input
                    type="text"
                    value={formData.menstrual_cycle}
                    onChange={(e) => handleInputChange('menstrual_cycle', e.target.value)}
                    placeholder="Describe your cycle..."
                  />
                </div>
              </div>
              <div className="inline-inputs">
                <div className="inline-group">
                  <label className="sub-label">Pregnancies/Births/Miscarriages</label>
                  <input
                    type="text"
                    value={formData.pregnancies}
                    onChange={(e) => handleInputChange('pregnancies', e.target.value)}
                    placeholder="e.g., 2 pregnancies, 2 live births"
                  />
                </div>
              </div>
              <div className="inline-inputs">
                <div className="inline-group">
                  <label className="sub-label">Perimenopause/Menopause Symptoms (if applicable)</label>
                  <input
                    type="text"
                    value={formData.menopause_symptoms}
                    onChange={(e) => handleInputChange('menopause_symptoms', e.target.value)}
                    placeholder="e.g., Hot flashes, mood changes"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section 6: Review of Systems */}
        {currentSection === 5 && (
          <div className="form-section" data-testid="section-review-systems">
            <h3>Section 6: Review of Systems (The Safety Net)</h3>
            <p className="section-intro">Please check any of the following you have experienced RECENTLY:</p>
            
            <div className="checkbox-grid">
              <div className="checkbox-group">
                <h4>Constitutional</h4>
                {['Unexplained weight gain/loss', 'Fevers', 'Night sweats'].map(item => (
                  <label key={item} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.constitutional.includes(item)}
                      onChange={() => handleCheckboxChange('constitutional', item)}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>

              <div className="checkbox-group">
                <h4>HEENT</h4>
                {['Brain fog', 'Headaches', 'Dry eyes', 'Dry mouth'].map(item => (
                  <label key={item} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.heent.includes(item)}
                      onChange={() => handleCheckboxChange('heent', item)}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>

              <div className="checkbox-group">
                <h4>Endocrine</h4>
                {['Heat/Cold intolerance', 'Hair loss', 'Skin changes'].map(item => (
                  <label key={item} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.endocrine.includes(item)}
                      onChange={() => handleCheckboxChange('endocrine', item)}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>

              <div className="checkbox-group">
                <h4>GI</h4>
                {['Bloating', 'Gas', 'Heartburn', 'Constipation', 'Diarrhea'].map(item => (
                  <label key={item} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.gi.includes(item)}
                      onChange={() => handleCheckboxChange('gi', item)}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>

              <div className="checkbox-group">
                <h4>Neuro/Psych</h4>
                {['Anxiety', 'Depression', 'Irritability', 'Memory issues'].map(item => (
                  <label key={item} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.neuropsych.includes(item)}
                      onChange={() => handleCheckboxChange('neuropsych', item)}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>

              <div className="checkbox-group">
                <h4>Other</h4>
                {['Joint pain', 'Muscle aches', 'Skin rashes'].map(item => (
                  <label key={item} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.other_symptoms.includes(item)}
                      onChange={() => handleCheckboxChange('other_symptoms', item)}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Section 7: Psychological & Behavioral Insights */}
        {currentSection === 6 && (
          <div className="form-section psychological-section" data-testid="section-psychological">
            <h3>Section 7: Your Perspective & Concerns</h3>
            <p className="section-intro">
              Understanding your thoughts, feelings, and concerns about your health is just as important as your symptoms. 
              This helps us provide truly personalized advocacy. There are no wrong answers—we&apos;re here to listen without judgment.
            </p>
            
            <div className="form-group">
              <label>17. What do YOU think might be causing your symptoms?</label>
              <p className="form-hint">Even if you&apos;re not sure, share any suspicions, hunches, or patterns you&apos;ve noticed. Your intuition about your own body matters.</p>
              <textarea
                value={formData.suspected_cause}
                onChange={(e) => handleInputChange('suspected_cause', e.target.value)}
                placeholder="I think it might be... / I've noticed it happens when... / I suspect..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>18. What are your biggest fears or worries about your health?</label>
              <p className="form-hint">It&apos;s okay to share concerns that might seem unlikely or embarrassing. Understanding your fears helps us address them.</p>
              <textarea
                value={formData.health_fears}
                onChange={(e) => handleInputChange('health_fears', e.target.value)}
                placeholder="I'm worried that... / My biggest fear is... / I can't stop thinking about..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>19. Have you experienced a health crisis involving someone close to you?</label>
              <p className="form-hint">Sometimes we worry about having the same condition as a friend or family member who became seriously ill or passed away. This is very common and important for us to know.</p>
              <textarea
                value={formData.family_health_experiences}
                onChange={(e) => handleInputChange('family_health_experiences', e.target.value)}
                placeholder="My mother/friend/colleague had... / I watched someone go through... / I lost someone to..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>20. What concerns have you read or heard about regarding your diagnosis?</label>
              <p className="form-hint">Information from the internet, friends, or media can be overwhelming. Share what you&apos;ve learned and what worries you—even if you&apos;re not sure it&apos;s accurate.</p>
              <textarea
                value={formData.information_sources}
                onChange={(e) => handleInputChange('information_sources', e.target.value)}
                placeholder="I read online that... / Someone told me... / I saw a story about..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>21. Is there anything about your current diagnosis you find hard to accept or understand?</label>
              <p className="form-hint">Many people struggle with denial, confusion, or disbelief about their diagnoses. This is completely normal.</p>
              <textarea
                value={formData.diagnosis_concerns}
                onChange={(e) => handleInputChange('diagnosis_concerns', e.target.value)}
                placeholder="I don't understand why... / It doesn't make sense that... / I have trouble believing..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>22. What makes it difficult for you to follow your treatment plan?</label>
              <p className="form-hint">Barriers to taking medication, following diets, or attending appointments are common. Be honest about what gets in the way.</p>
              <textarea
                value={formData.adherence_challenges}
                onChange={(e) => handleInputChange('adherence_challenges', e.target.value)}
                placeholder="I struggle to... / I often forget... / I stop taking my medication because..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>23. Do you trust your current healthcare providers? Why or why not?</label>
              <p className="form-hint">Trust issues with doctors are more common than you might think. Your feelings are valid and help us understand how to best support you.</p>
              <textarea
                value={formData.trust_barriers}
                onChange={(e) => handleInputChange('trust_barriers', e.target.value)}
                placeholder="I feel like my doctor... / I don't trust... / I wish my provider would..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>24. How has your health situation affected you emotionally?</label>
              <p className="form-hint">Physical health and emotional wellbeing are deeply connected. How are you really doing?</p>
              <textarea
                value={formData.emotional_impact}
                onChange={(e) => handleInputChange('emotional_impact', e.target.value)}
                placeholder="I feel... / This has made me... / Emotionally, I'm..."
                rows="3"
              />
            </div>

            <div className="psychological-note">
              <p><strong>A Note from CADENCE:</strong> Thank you for sharing these deeply personal insights. Your honesty helps us see the full picture—not just your symptoms, but your experience as a whole person navigating the healthcare system. We honor your trust.</p>
            </div>
          </div>
        )}

        {/* Section 8: Authorization */}
        {currentSection === 7 && (
          <div className="form-section" data-testid="section-authorization">
            <h3>Section 8: Authorization & Consent</h3>
            
            {/* Early Supporter Notice - Package Selection Hidden */}
            <div className="early-supporter-notice">
              <div className="notice-badge">Early Supporter Access</div>
              <p>Thank you for being among our first supporters! Complete this health survey to unlock full site access, including our membership plans and pricing information.</p>
            </div>

            <div className="authorization-box">
              <h4>Authorization</h4>
              <p>I authorize CADENCE Medical Consulting, LLC to view and analyze the contents of my health survey record submitted for the purpose of providing consultation and personalized recommendations.</p>
              
              <div className="form-group">
                <label className="checkbox-item consent-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.consent_given}
                    onChange={(e) => handleInputChange('consent_given', e.target.checked)}
                    data-testid="consent-checkbox"
                  />
                  <span>I consent to the above authorization</span>
                </label>
              </div>

              <div className="form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-light)' }}>
                <label className="checkbox-item consent-checkbox refund-policy-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.refund_policy_acknowledged}
                    onChange={(e) => handleInputChange('refund_policy_acknowledged', e.target.checked)}
                    data-testid="refund-policy-checkbox"
                  />
                  <span>
                    I have read and understand the{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>
                      Terms of Service & Privacy Policy
                    </a>
                    . I acknowledge that my health survey information will be used solely for consultation purposes.
                  </span>
                </label>
              </div>

              <div className="signature-group">
                <div className="form-group">
                  <label>Printed Name / Electronic Signature</label>
                  <input
                    type="text"
                    value={formData.signature}
                    onChange={(e) => handleInputChange('signature', e.target.value)}
                    placeholder="Type your full name"
                    data-testid="signature-input"
                  />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={formData.signature_date}
                    onChange={(e) => handleInputChange('signature_date', e.target.value)}
                    data-testid="signature-date"
                  />
                </div>
              </div>
            </div>

            <div className="records-request">
              <h4>What happens next?</h4>
              <ul>
                <li>Your health survey will be securely stored</li>
                <li>You&apos;ll gain full access to explore our site</li>
                <li>Browse our membership plans and pricing</li>
                <li>When ready, schedule your consultation</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="intake-navigation">
        <button
          className="btn-outline"
          onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
          disabled={currentSection === 0}
        >
          Previous
        </button>
        
        {currentSection < sections.length - 1 ? (
          <button
            className="btn-primary"
            onClick={() => setCurrentSection(prev => prev + 1)}
          >
            Next Section
          </button>
        ) : (
          <button
            className="btn-gold"
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.consent_given || !formData.refund_policy_acknowledged || !formData.signature}
            data-testid="submit-intake-btn"
          >
            {isSubmitting ? 'Submitting...' : 'Complete Survey & Unlock Site'}
          </button>
        )}
      </div>
    </div>
  );
};

// Profile View Component
const ProfileView = ({ user, token }) => {
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(true);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [regeneratePassword, setRegeneratePassword] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [backupCodesCount, setBackupCodesCount] = useState(0);
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFASuccess, setTwoFASuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Change Password state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }
    
    setPasswordLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          current_password: currentPassword, 
          new_password: newPassword 
        })
      });
      const data = await response.json();
      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setShowChangePasswordModal(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        setPasswordError(data.detail || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError('Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Fetch 2FA status and backup codes count on mount
  useEffect(() => {
    const fetch2FAStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/2fa/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTwoFAEnabled(data.enabled);
          if (data.enabled) {
            // Fetch backup codes count
            const countResponse = await fetch(`${API_URL}/api/auth/2fa/backup-codes-count`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (countResponse.ok) {
              const countData = await countResponse.json();
              setBackupCodesCount(countData.count);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch 2FA status:', err);
      } finally {
        setTwoFALoading(false);
      }
    };
    fetch2FAStatus();
  }, [token]);

  const handleEnable2FA = async () => {
    setActionLoading(true);
    setTwoFAError('');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/enable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setTwoFASuccess(`Code sent to ${data.email_masked}`);
        setShowEnableModal(true);
      } else {
        setTwoFAError(data.detail || 'Failed to enable 2FA');
      }
    } catch (err) {
      setTwoFAError('Failed to enable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify2FASetup = async () => {
    setActionLoading(true);
    setTwoFAError('');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/verify-setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: verificationCode })
      });
      const data = await response.json();
      if (response.ok) {
        setTwoFAEnabled(true);
        setShowEnableModal(false);
        setVerificationCode('');
        setBackupCodes(data.backup_codes || []);
        setBackupCodesCount(data.backup_codes?.length || 0);
        setShowBackupCodesModal(true);
      } else {
        setTwoFAError(data.detail || 'Invalid verification code');
      }
    } catch (err) {
      setTwoFAError('Failed to verify code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setActionLoading(true);
    setTwoFAError('');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: disablePassword })
      });
      const data = await response.json();
      if (response.ok) {
        setTwoFAEnabled(false);
        setShowDisableModal(false);
        setDisablePassword('');
        setBackupCodesCount(0);
        setTwoFASuccess('Two-factor authentication disabled');
      } else {
        setTwoFAError(data.detail || 'Failed to disable 2FA');
      }
    } catch (err) {
      setTwoFAError('Failed to disable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setActionLoading(true);
    setTwoFAError('');
    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/regenerate-backup-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: regeneratePassword })
      });
      const data = await response.json();
      if (response.ok) {
        setShowRegenerateModal(false);
        setRegeneratePassword('');
        setBackupCodes(data.backup_codes || []);
        setBackupCodesCount(data.backup_codes?.length || 0);
        setShowBackupCodesModal(true);
      } else {
        setTwoFAError(data.detail || 'Failed to regenerate backup codes');
      }
    } catch (err) {
      setTwoFAError('Failed to regenerate backup codes');
    } finally {
      setActionLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setTwoFASuccess('Backup codes copied to clipboard!');
  };

  return (
    <div className="profile-view">
      <div className="profile-card">
        <div className="profile-header">
          <div className="avatar">
            {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
          </div>
          <div className="profile-info">
            <h2>{user?.first_name} {user?.last_name}</h2>
            <p>{user?.email}</p>
          </div>
        </div>
        
        <div className="profile-sections">
          <div className="section">
            <h3>Contact Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Email</span>
                <span className="value">{user?.email}</span>
              </div>
              <div className="info-item">
                <span className="label">Phone</span>
                <span className="value">{user?.phone || 'Not provided'}</span>
              </div>
            </div>
          </div>

          <div className="section">
            <h3>Security & Privacy</h3>
            
            {/* 2FA Success/Error Messages */}
            {twoFASuccess && (
              <div className="twofa-message success">
                <CheckCircle size={16} />
                {twoFASuccess}
              </div>
            )}
            {twoFAError && !showEnableModal && !showDisableModal && (
              <div className="twofa-message error">
                <AlertCircle size={16} />
                {twoFAError}
              </div>
            )}
            
            <div className="security-items">
              {/* 2FA Setting */}
              <div className="security-item twofa-setting">
                <ShieldCheck size={20} />
                <div className="security-content">
                  <span className="title">Two-Factor Authentication</span>
                  <span className="description">
                    {twoFAEnabled 
                      ? 'Your account is protected with email verification codes' 
                      : 'Add an extra layer of security to your account'}
                  </span>
                </div>
                <div className="security-action">
                  {twoFALoading ? (
                    <span className="loading-indicator">Loading...</span>
                  ) : twoFAEnabled ? (
                    <button 
                      className="btn-outline danger"
                      onClick={() => setShowDisableModal(true)}
                      data-testid="disable-2fa-btn"
                    >
                      Disable
                    </button>
                  ) : (
                    <button 
                      className="btn-primary small"
                      onClick={handleEnable2FA}
                      disabled={actionLoading}
                      data-testid="enable-2fa-btn"
                    >
                      {actionLoading ? 'Enabling...' : 'Enable'}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Backup Codes (only show when 2FA is enabled) */}
              {twoFAEnabled && (
                <div className="security-item backup-codes-setting">
                  <Key size={20} />
                  <div className="security-content">
                    <span className="title">Backup Codes</span>
                    <span className="description">
                      {backupCodesCount} codes remaining for account recovery
                      {backupCodesCount <= 2 && backupCodesCount > 0 && (
                        <span className="warning-badge"> - Running low!</span>
                      )}
                    </span>
                  </div>
                  <div className="security-action">
                    <button 
                      className="btn-outline small"
                      onClick={() => setShowRegenerateModal(true)}
                      disabled={actionLoading}
                      data-testid="regenerate-backup-codes-btn"
                    >
                      Regenerate
                    </button>
                  </div>
                </div>
              )}
              
              {/* Change Password */}
              <div className="security-item change-password-setting">
                <Lock size={20} />
                <div className="security-content">
                  <span className="title">Password</span>
                  <span className="description">Change your account password</span>
                </div>
                <div className="security-action">
                  <button 
                    className="btn-outline small"
                    onClick={() => setShowChangePasswordModal(true)}
                    data-testid="change-password-btn"
                  >
                    Change
                  </button>
                </div>
              </div>
              
              <div className="security-item">
                <Shield size={20} />
                <div>
                  <span className="title">HIPAA Consent</span>
                  <span className="status signed">Signed</span>
                </div>
              </div>
              <div className="security-item">
                <Clock size={20} />
                <div>
                  <span className="title">Last Login</span>
                  <span className="status">Today</span>
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <h3>Access Log</h3>
            <p className="access-log-info">
              View who has accessed your medical information for HIPAA compliance.
            </p>
            <button className="btn-outline">View Access Log</button>
          </div>
        </div>
      </div>

      {/* Enable 2FA Modal */}
      {showEnableModal && (
        <div className="modal-overlay" onClick={() => setShowEnableModal(false)}>
          <div className="modal twofa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <ShieldCheck size={24} />
              <h3>Enable Two-Factor Authentication</h3>
            </div>
            <div className="modal-body">
              <p>Enter the 6-digit verification code sent to your email:</p>
              {twoFAError && (
                <div className="twofa-message error">
                  <AlertCircle size={16} />
                  {twoFAError}
                </div>
              )}
              <div className="twofa-code-input-modal">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                    setTwoFAError('');
                  }}
                  placeholder="000000"
                  maxLength={6}
                  data-testid="2fa-setup-code-input"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-outline"
                onClick={() => {
                  setShowEnableModal(false);
                  setVerificationCode('');
                  setTwoFAError('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleVerify2FASetup}
                disabled={actionLoading || verificationCode.length !== 6}
                data-testid="2fa-setup-verify-btn"
              >
                {actionLoading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable 2FA Modal */}
      {showDisableModal && (
        <div className="modal-overlay" onClick={() => setShowDisableModal(false)}>
          <div className="modal twofa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header warning">
              <AlertTriangle size={24} />
              <h3>Disable Two-Factor Authentication</h3>
            </div>
            <div className="modal-body">
              <p>Enter your password to confirm disabling 2FA:</p>
              {twoFAError && (
                <div className="twofa-message error">
                  <AlertCircle size={16} />
                  {twoFAError}
                </div>
              )}
              <div className="password-input-modal">
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => {
                    setDisablePassword(e.target.value);
                    setTwoFAError('');
                  }}
                  placeholder="Enter your password"
                  data-testid="2fa-disable-password-input"
                />
              </div>
              <p className="warning-text">
                <AlertTriangle size={14} />
                This will make your account less secure.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-outline"
                onClick={() => {
                  setShowDisableModal(false);
                  setDisablePassword('');
                  setTwoFAError('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleDisable2FA}
                disabled={actionLoading || !disablePassword}
                data-testid="2fa-disable-confirm-btn"
              >
                {actionLoading ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup Codes Display Modal */}
      {showBackupCodesModal && (
        <div className="modal-overlay">
          <div className="modal twofa-modal backup-codes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header success">
              <ShieldCheck size={24} />
              <h3>Save Your Backup Codes</h3>
            </div>
            <div className="modal-body">
              <div className="backup-codes-warning">
                <AlertTriangle size={16} />
                <p>Save these codes in a secure location. Each code can only be used once. You won't see them again!</p>
              </div>
              <div className="backup-codes-grid">
                {backupCodes.map((code, index) => (
                  <div key={index} className="backup-code-item">
                    <code>{code}</code>
                  </div>
                ))}
              </div>
              <button 
                className="btn-outline copy-codes-btn"
                onClick={copyBackupCodes}
              >
                <Copy size={16} />
                Copy All Codes
              </button>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-primary"
                onClick={() => {
                  setShowBackupCodesModal(false);
                  setBackupCodes([]);
                  setTwoFASuccess('Two-factor authentication is now enabled!');
                }}
                data-testid="backup-codes-done-btn"
              >
                I've Saved My Codes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Backup Codes Modal */}
      {showRegenerateModal && (
        <div className="modal-overlay" onClick={() => setShowRegenerateModal(false)}>
          <div className="modal twofa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <Key size={24} />
              <h3>Regenerate Backup Codes</h3>
            </div>
            <div className="modal-body">
              <p>Enter your password to generate new backup codes:</p>
              {twoFAError && (
                <div className="twofa-message error">
                  <AlertCircle size={16} />
                  {twoFAError}
                </div>
              )}
              <div className="password-input-modal">
                <input
                  type="password"
                  value={regeneratePassword}
                  onChange={(e) => {
                    setRegeneratePassword(e.target.value);
                    setTwoFAError('');
                  }}
                  placeholder="Enter your password"
                  data-testid="regenerate-password-input"
                />
              </div>
              <p className="info-text">
                <Info size={14} />
                This will invalidate all your existing backup codes.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-outline"
                onClick={() => {
                  setShowRegenerateModal(false);
                  setRegeneratePassword('');
                  setTwoFAError('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleRegenerateBackupCodes}
                disabled={actionLoading || !regeneratePassword}
                data-testid="regenerate-confirm-btn"
              >
                {actionLoading ? 'Generating...' : 'Generate New Codes'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="modal-overlay" onClick={() => setShowChangePasswordModal(false)}>
          <div className="modal twofa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <Lock size={24} />
              <h3>Change Password</h3>
            </div>
            <div className="modal-body">
              {passwordSuccess && (
                <div className="twofa-message success">
                  <CheckCircle size={16} />
                  {passwordSuccess}
                </div>
              )}
              {passwordError && (
                <div className="twofa-message error">
                  <AlertCircle size={16} />
                  {passwordError}
                </div>
              )}
              <div className="password-input-modal">
                <label>Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
              </div>
              <div className="password-input-modal">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="Enter new password (min 8 characters)"
                  data-testid="new-password-input"
                />
              </div>
              <div className="password-input-modal">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-outline"
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleChangePassword}
                disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                data-testid="change-password-confirm-btn"
              >
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portal;
